/**
 * Infrastructure Page
 *
 * GPU fleet management for cost control.
 * Provides buttons to:
 * - Deploy/Destroy EC2 and ECS infrastructure
 * - Start/stop/terminate the entire fleet or individual instances
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useProgress } from '../contexts/ProgressContext';
import {
  Server,
  Play,
  Pause,
  Power,
  DollarSign,
  XCircle,
  RefreshCw,
  TrendingDown,
  Clock,
  Globe,
  Cpu,
  Activity,
  Loader2,
  Cloud,
  CloudOff,
  Box,
  Layers,
  Plus,
  Minus,
  Container,
  Settings2,
  BarChart3,
  Square,
  ChevronRight,
  ChevronLeft,
  Filter,
  AlertTriangle,
  Trash2,
  Moon,
  HardDrive,
  Shield,
  Network,
  Tag,
  Key,
  Bell,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';
import {
  infrastructureApi,
  deploymentApi,
  ecsApi,
  gpuJobsApi,
  apiKeysApi,
  detailedNetworkApi,
  FleetActionResult,
  DeploymentResult,
  EcsTaskInfo,
  ApiKeyScope,
  NewApiKey,
} from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import NetworkPanel from '../components/NetworkPanel';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
};

type ConfirmAction = 'start' | 'stop' | 'terminate' | 'deploy-ec2' | 'deploy-ecs' | 'destroy-ec2' | 'destroy-ecs' | 'run-ecs-task' | 'stop-ecs-task' | 'cleanup-stuck-tasks' | 'scale-ecs' | 'repair-ecs' | 'cancel-ec2' | 'cancel-ecs' | 'terminate-instance' | 'terminate-instances-bulk' | 'delete-ecs-cluster' | 'remove-gpu-job' | null;

// Constants for Create Instance Form
const INSTANCE_TYPES = [
  // Free Tier eligible (no GPU)
  { type: 't2.micro', gpus: 0, memory: 1, description: 'Testing only', freeTier: true, cost: 'FREE' },
  { type: 't3.micro', gpus: 0, memory: 1, description: 'Testing only', freeTier: true, cost: 'FREE' },
  // GPU instances
  { type: 'g4dn.xlarge', gpus: 1, memory: 16, description: 'NVIDIA T4', freeTier: false, cost: '~$0.53/hr' },
  { type: 'g5.xlarge', gpus: 1, memory: 16, description: 'NVIDIA A10G', freeTier: false, cost: '~$1.01/hr' },
  { type: 'g5.2xlarge', gpus: 1, memory: 32, description: 'NVIDIA A10G', freeTier: false, cost: '~$1.21/hr' },
  { type: 'g5.4xlarge', gpus: 1, memory: 64, description: 'High memory', freeTier: false, cost: '~$1.62/hr' },
  { type: 'g5.8xlarge', gpus: 1, memory: 128, description: 'Extra memory', freeTier: false, cost: '~$2.45/hr' },
  { type: 'g5.12xlarge', gpus: 4, memory: 192, description: 'Multi-GPU', freeTier: false, cost: '~$5.67/hr' },
  { type: 'p3.2xlarge', gpus: 1, memory: 61, description: 'NVIDIA V100', freeTier: false, cost: '~$3.06/hr' },
];

// Pre-configured Docker images for ML/AI workloads
const DOCKER_IMAGES = [
  { value: '', label: 'Default CUDA Image', description: 'nvidia/cuda:12.2.0-base-ubuntu22.04' },
  { value: 'pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime', label: 'PyTorch 2.1', description: 'CUDA 12.1 + cuDNN 8' },
  { value: 'pytorch/pytorch:2.0.1-cuda11.7-cudnn8-runtime', label: 'PyTorch 2.0', description: 'CUDA 11.7 + cuDNN 8' },
  { value: 'tensorflow/tensorflow:2.14.0-gpu', label: 'TensorFlow 2.14', description: 'GPU enabled' },
  { value: 'tensorflow/tensorflow:2.13.0-gpu', label: 'TensorFlow 2.13', description: 'GPU enabled' },
  { value: 'huggingface/transformers-pytorch-gpu:latest', label: 'HuggingFace Transformers', description: 'PyTorch GPU' },
  { value: 'nvcr.io/nvidia/pytorch:23.10-py3', label: 'NVIDIA PyTorch NGC', description: 'Optimized container' },
  { value: 'nvcr.io/nvidia/tensorflow:23.10-tf2-py3', label: 'NVIDIA TensorFlow NGC', description: 'Optimized container' },
  { value: 'jupyter/tensorflow-notebook:latest', label: 'Jupyter + TensorFlow', description: 'Notebook environment' },
  { value: 'jupyter/pytorch-notebook:latest', label: 'Jupyter + PyTorch', description: 'Notebook environment' },
  { value: 'custom', label: 'Custom Image...', description: 'Enter custom Docker image' },
];

// Volume size presets
const VOLUME_SIZES = [
  { value: 50, label: '50 GB', description: 'Small workloads' },
  { value: 100, label: '100 GB', description: 'Standard (default)' },
  { value: 200, label: '200 GB', description: 'Medium datasets' },
  { value: 500, label: '500 GB', description: 'Large datasets' },
  { value: 1000, label: '1 TB', description: 'Very large datasets' },
];

// AWS Availability Zones (us-east-1 as example, dynamically loaded in production)
const AVAILABILITY_ZONES = [
  { value: '', label: 'Auto (Any AZ)' },
  { value: 'us-east-1a', label: 'us-east-1a' },
  { value: 'us-east-1b', label: 'us-east-1b' },
  { value: 'us-east-1c', label: 'us-east-1c' },
  { value: 'us-east-1d', label: 'us-east-1d' },
  { value: 'us-east-1e', label: 'us-east-1e' },
  { value: 'us-east-1f', label: 'us-east-1f' },
];

// Idle timeout presets
const IDLE_TIMEOUTS = [
  { value: 0, label: 'Disabled' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

// Max cost presets
const MAX_COST_PRESETS = [
  { value: 0, label: 'No limit' },
  { value: 5, label: '$5' },
  { value: 10, label: '$10' },
  { value: 25, label: '$25' },
  { value: 50, label: '$50' },
  { value: 100, label: '$100' },
  { value: 250, label: '$250' },
  { value: 500, label: '$500' },
];

// Instance List Component
function InstanceList({ 
  clusterName, 
  selectedInstances, 
  setSelectedInstances, 
  onTerminate 
}: { 
  clusterName: string; 
  selectedInstances: Set<string>; 
  setSelectedInstances: (instances: Set<string>) => void;
  onTerminate: (instanceId: string) => void;
}) {
  const { data: clusterDetails, isLoading } = useQuery({
    queryKey: ['ecs-cluster-details', clusterName],
    queryFn: () => ecsApi.getClusterDetails(clusterName),
    refetchInterval: 10000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gpu-400" />
      </div>
    );
  }

  if (!clusterDetails?.containerInstances || clusterDetails.containerInstances.length === 0) {
    return (
      <div className="text-center py-6 text-surface-400">
        <Server className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No instances available</p>
      </div>
    );
  }

  const toggleInstance = (instanceId: string) => {
    const newSelected = new Set(selectedInstances);
    if (newSelected.has(instanceId)) {
      newSelected.delete(instanceId);
    } else {
      newSelected.add(instanceId);
    }
    setSelectedInstances(newSelected);
  };

  const toggleAll = () => {
    if (selectedInstances.size === clusterDetails.containerInstances!.length) {
      setSelectedInstances(new Set());
    } else {
      setSelectedInstances(new Set(clusterDetails.containerInstances!.map(i => i.ec2InstanceId)));
    }
  };

  return (
    <div className="space-y-2">
      {/* Select All Checkbox */}
      <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-800/30 border border-surface-700/50">
        <input
          type="checkbox"
          checked={selectedInstances.size === clusterDetails.containerInstances.length && clusterDetails.containerInstances.length > 0}
          onChange={toggleAll}
          className="w-4 h-4 rounded border-surface-600"
        />
        <span className="text-sm text-surface-300 font-medium">Select All</span>
      </div>

      {/* Instance List */}
      {clusterDetails.containerInstances.map((instance) => (
        <div
          key={instance.ec2InstanceId}
          className={clsx(
            'p-3 rounded-lg border transition-colors',
            selectedInstances.has(instance.ec2InstanceId)
              ? 'bg-gpu-500/10 border-gpu-500/30'
              : 'bg-surface-800/50 border-surface-700/50'
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <input
                type="checkbox"
                checked={selectedInstances.has(instance.ec2InstanceId)}
                onChange={() => toggleInstance(instance.ec2InstanceId)}
                className="w-4 h-4 rounded border-surface-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm text-surface-100">
                    {instance.ec2InstanceId}
                  </span>
                  <span className={clsx(
                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                    instance.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' :
                    instance.status === 'DRAINING' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-surface-600/50 text-surface-300'
                  )}>
                    <span className={clsx(
                      'w-1.5 h-1.5 rounded-full',
                      instance.status === 'ACTIVE' && 'bg-emerald-400 animate-pulse'
                    )} />
                    {instance.status}
                  </span>
                  {instance.agentConnected && (
                    <span className="text-xs text-surface-500">• Agent Connected</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-surface-400">
                  <span>Running: {instance.runningTasksCount}</span>
                  <span>Pending: {instance.pendingTasksCount}</span>
                  {instance.registeredAt && (
                    <span>Registered: {formatDistanceToNow(new Date(instance.registeredAt), { addSuffix: true })}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => onTerminate(instance.ec2InstanceId)}
              className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              title="Terminate instance"
            >
              <Power className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Task List Component for managing tasks per cluster
function TaskList({ 
  clusterName,
  onStopTask,
  onCleanupStuckTasks,
  isLoading: externalLoading 
}: { 
  clusterName: string;
  onStopTask: (clusterName: string, task: EcsTaskInfo) => void;
  onCleanupStuckTasks: (clusterName: string, tasks: EcsTaskInfo[]) => void;
  isLoading?: boolean;
}) {
  const [showHistory, setShowHistory] = useState(false);
  
  const { data: clusterDetails, isLoading } = useQuery({
    queryKey: ['ecs-cluster-details', clusterName],
    queryFn: () => ecsApi.getClusterDetails(clusterName),
    refetchInterval: 10000,
  });

  // Fetch task history when toggled
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['ecs-tasks-history', clusterName],
    queryFn: () => ecsApi.getTasksHistory(clusterName),
    enabled: showHistory,
    refetchInterval: showHistory ? 15000 : false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gpu-400" />
      </div>
    );
  }

  const runningTasks = clusterDetails?.tasks || [];
  const pendingTasks = runningTasks.filter(t => t.lastStatus === 'PENDING');
  const historyTasks = historyData?.tasks || [];

  // Merge running tasks with history, avoiding duplicates
  const displayTasks = showHistory ? historyTasks : runningTasks;

  if (displayTasks.length === 0 && !showHistory) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-end">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs px-3 py-1.5 rounded-lg bg-surface-700/50 text-surface-300 hover:bg-surface-700 transition-colors flex items-center gap-1.5"
          >
            <Clock className="w-3 h-3" />
            {showHistory ? 'Hide History' : 'Show History'}
          </button>
        </div>
        <div className="text-center py-6 text-surface-400">
          <Container className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No tasks running</p>
          {clusterDetails?.registeredContainerInstancesCount === 0 && (
            <p className="text-xs text-surface-500 mt-1">Scale up to run tasks</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toggle History Button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-surface-400">
          {showHistory ? `Showing ${displayTasks.length} recent task(s)` : `${runningTasks.length} active task(s)`}
        </span>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className={clsx(
            "text-xs px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5",
            showHistory 
              ? "bg-gpu-500/20 text-gpu-300 border border-gpu-500/30" 
              : "bg-surface-700/50 text-surface-300 hover:bg-surface-700"
          )}
        >
          {historyLoading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Clock className="w-3 h-3" />
          )}
          {showHistory ? 'Showing All History' : 'Show History'}
        </button>
      </div>

      {/* Cleanup Stuck Tasks Button */}
      {pendingTasks.length > 0 && !showHistory && (
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-300">
                {pendingTasks.length} pending task(s) waiting for resources
              </span>
            </div>
            <button
              onClick={() => onCleanupStuckTasks(clusterName, pendingTasks)}
              disabled={externalLoading}
              className="text-xs px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors flex items-center gap-1.5"
            >
              {externalLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <XCircle className="w-3 h-3" />
              )}
              Stop All Pending
            </button>
          </div>
        </div>
      )}

      {/* Task List */}
      {displayTasks.length === 0 && showHistory && historyLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gpu-400" />
        </div>
      )}
      
      {displayTasks.length === 0 && showHistory && !historyLoading && (
        <div className="text-center py-6 text-surface-400">
          <Container className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No task history available</p>
        </div>
      )}

      {displayTasks.map((task) => (
        <div
          key={task.taskArn}
          className={clsx(
            "p-3 rounded-lg border",
            task.lastStatus === 'STOPPED' 
              ? "bg-surface-800/30 border-surface-700/30" 
              : "bg-surface-800/50 border-surface-700/50"
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={clsx(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border',
                  task.lastStatus === 'RUNNING' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                  task.lastStatus === 'PENDING' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                  task.lastStatus === 'STOPPED' ? 'bg-surface-600/50 text-surface-300 border-surface-500/30' :
                  'bg-blue-500/20 text-blue-300 border-blue-500/30'
                )}>
                  <span className={clsx(
                    'w-1.5 h-1.5 rounded-full',
                    task.lastStatus === 'RUNNING' ? 'bg-emerald-400 animate-pulse' :
                    task.lastStatus === 'PENDING' ? 'bg-yellow-400 animate-pulse' :
                    task.lastStatus === 'STOPPED' ? 'bg-red-400' :
                    'bg-surface-400'
                  )} />
                  {task.lastStatus}
                </span>
                <span className="font-mono text-sm text-surface-400">
                  {task.taskArn.split('/').pop()?.slice(0, 12)}...
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-surface-400">
                {task.cpu && <span>{task.cpu} CPU</span>}
                {task.memory && <span>{task.memory} MB</span>}
                {task.launchType && <span>{task.launchType}</span>}
                {task.startedAt && (
                  <span>Started {formatDistanceToNow(new Date(task.startedAt), { addSuffix: true })}</span>
                )}
                {task.stoppedAt && (
                  <span className="text-red-400">Stopped {formatDistanceToNow(new Date(task.stoppedAt), { addSuffix: true })}</span>
                )}
              </div>
              {task.stoppedReason && (
                <div className="mt-1 text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">
                  <span className="font-medium">Reason:</span> {task.stoppedReason}
                </div>
              )}
            </div>
            {(task.lastStatus === 'RUNNING' || task.lastStatus === 'PENDING') && (
              <button
                onClick={() => onStopTask(clusterName, task)}
                disabled={externalLoading}
                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Stop task"
              >
                {externalLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Topology View Component
function TopologyView({ cluster }: { cluster: { clusterArn: string; clusterName: string; status: string; runningTasksCount: number; pendingTasksCount: number; registeredContainerInstancesCount: number; } }) {
  const { data: clusterDetails, isLoading } = useQuery({
    queryKey: ['ecs-cluster-details', cluster.clusterName],
    queryFn: () => ecsApi.getClusterDetails(cluster.clusterName),
    refetchInterval: 10000,
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gpu-400" />
        </div>
      </div>
    );
  }

  if (!clusterDetails) {
    return null;
  }

  const instances = clusterDetails.containerInstances || [];
  const tasks = clusterDetails.tasks || [];

  // Group tasks by container instance
  const tasksByInstance = new Map<string, typeof tasks>();
  tasks.forEach(task => {
    if (task.containerInstanceArn) {
      const existing = tasksByInstance.get(task.containerInstanceArn) || [];
      tasksByInstance.set(task.containerInstanceArn, [...existing, task]);
    }
  });

  // Tasks not assigned to any instance (pending)
  const unassignedTasks = tasks.filter(task => !task.containerInstanceArn);

  return (
    <div className="card">
      {/* Cluster Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-surface-700">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-surface-100">{cluster.clusterName}</h3>
            <div className="flex items-center gap-4 mt-1">
              <span className={clsx(
                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium',
                cluster.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
              )}>
                <span className={clsx(
                  'w-1.5 h-1.5 rounded-full',
                  cluster.status === 'ACTIVE' && 'bg-emerald-400 animate-pulse'
                )} />
                {cluster.status}
              </span>
              <span className="text-xs text-surface-400">
                {instances.length} instances • {cluster.runningTasksCount} running • {cluster.pendingTasksCount} pending
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Topology Visualization */}
      <div className="space-y-6">
        {/* Cluster Level */}
        <div className="flex items-center justify-center">
          <div className="px-6 py-3 rounded-lg bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-500/30">
            <div className="flex items-center gap-3">
              <Layers className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-sm font-semibold text-surface-100">ECS Cluster</div>
                <div className="text-xs text-surface-400">{cluster.clusterName}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Line */}
        {instances.length > 0 && (
          <div className="flex justify-center">
            <div className="w-0.5 h-8 bg-gradient-to-b from-blue-500/50 to-purple-500/50" />
          </div>
        )}

        {/* Container Instances Level */}
        {instances.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {instances.map((instance) => {
              const instanceTasks = tasksByInstance.get(instance.containerInstanceArn) || [];
              const runningTasks = instanceTasks.filter(t => t.lastStatus === 'RUNNING');
              const pendingTasks = instanceTasks.filter(t => t.lastStatus === 'PENDING' || t.lastStatus === 'PROVISIONING');

              return (
                <div key={instance.containerInstanceArn} className="relative">
                  {/* Instance Card */}
                  <div className={clsx(
                    'p-4 rounded-lg border-2 transition-all',
                    instance.status === 'ACTIVE' && instance.agentConnected
                      ? 'bg-gradient-to-br from-emerald-600/10 to-green-600/10 border-emerald-500/30'
                      : 'bg-gradient-to-br from-surface-800/50 to-surface-700/50 border-surface-600/30'
                  )}>
                    {/* Instance Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Server className={clsx(
                        'w-5 h-5',
                        instance.status === 'ACTIVE' && instance.agentConnected ? 'text-emerald-400' : 'text-surface-400'
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-surface-100 truncate">
                          {instance.ec2InstanceId}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={clsx(
                            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs',
                            instance.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
                          )}>
                            <span className={clsx(
                              'w-1 h-1 rounded-full',
                              instance.status === 'ACTIVE' && 'bg-emerald-400'
                            )} />
                            {instance.status}
                          </span>
                          {instance.agentConnected ? (
                            <div title="Agent Connected">
                              <Activity className="w-3 h-3 text-emerald-400" />
                            </div>
                          ) : (
                            <div title="Agent Disconnected">
                              <AlertTriangle className="w-3 h-3 text-yellow-400" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Instance Stats */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="p-2 rounded bg-surface-900/50">
                        <div className="text-xs text-surface-400">Running</div>
                        <div className="text-lg font-bold text-emerald-400">{runningTasks.length}</div>
                      </div>
                      <div className="p-2 rounded bg-surface-900/50">
                        <div className="text-xs text-surface-400">Pending</div>
                        <div className="text-lg font-bold text-yellow-400">{pendingTasks.length}</div>
                      </div>
                    </div>

                    {/* Tasks on this Instance */}
                    {instanceTasks.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-surface-300 mb-2 flex items-center gap-1">
                          <Box className="w-3 h-3" />
                          Tasks ({instanceTasks.length})
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar">
                          {instanceTasks.map((task) => {
                            const taskId = task.taskArn.split('/').pop()?.substring(0, 8) || 'unknown';
                            const isRunning = task.lastStatus === 'RUNNING';
                            const isPending = task.lastStatus === 'PENDING' || task.lastStatus === 'PROVISIONING';
                            const isStopped = task.lastStatus === 'STOPPED' || task.lastStatus === 'DEPROVISIONING';

                            return (
                              <div
                                key={task.taskArn}
                                className={clsx(
                                  'px-2 py-1.5 rounded text-xs flex items-center gap-2 border',
                                  isRunning && 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
                                  isPending && 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300',
                                  isStopped && 'bg-surface-700/30 border-surface-600/30 text-surface-400'
                                )}
                              >
                                <Box className="w-3 h-3 flex-shrink-0" />
                                <span className="flex-1 truncate font-mono">{taskId}</span>
                                <span className={clsx(
                                  'px-1.5 py-0.5 rounded text-xs font-medium',
                                  isRunning && 'bg-emerald-500/20 text-emerald-200',
                                  isPending && 'bg-yellow-500/20 text-yellow-200',
                                  isStopped && 'bg-surface-600/20 text-surface-300'
                                )}>
                                  {task.lastStatus}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {instanceTasks.length === 0 && (
                      <div className="text-center py-3 text-surface-500 text-xs">
                        <Box className="w-4 h-4 mx-auto mb-1 opacity-50" />
                        No tasks running
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-surface-400">
            <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No container instances available</p>
          </div>
        )}

        {/* Unassigned/Pending Tasks */}
        {unassignedTasks.length > 0 && (
          <>
            <div className="flex justify-center">
              <div className="w-0.5 h-8 bg-gradient-to-b from-purple-500/50 to-yellow-500/50" />
            </div>
            <div className="card bg-gradient-to-br from-yellow-600/10 to-orange-600/10 border-yellow-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-yellow-400" />
                <h4 className="text-sm font-semibold text-surface-100">Pending Tasks (Unassigned)</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {unassignedTasks.map((task) => {
                  const taskId = task.taskArn.split('/').pop()?.substring(0, 8) || 'unknown';
                  return (
                    <div
                      key={task.taskArn}
                      className="px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 flex items-center gap-2"
                    >
                      <Box className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 truncate text-xs font-mono">{taskId}</span>
                      <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/20">
                        {task.lastStatus}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-yellow-400/70 mt-3">
                These tasks are waiting for available container instances
              </p>
            </div>
          </>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-surface-700">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{cluster.runningTasksCount}</div>
            <div className="text-xs text-surface-400 mt-1">Running Tasks</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">{cluster.pendingTasksCount}</div>
            <div className="text-xs text-surface-400 mt-1">Pending Tasks</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{instances.length}</div>
            <div className="text-xs text-surface-400 mt-1">Total Instances</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InfrastructurePage() {
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [activeTab, setActiveTab] = useState<'deployment' | 'fleet' | 'gpu-containers'>('gpu-containers');
  const [deploymentSubTab, setDeploymentSubTab] = useState<'instances' | 'network' | 'api-keys'>('instances');
  const [gpuContainersSubTab, setGpuContainersSubTab] = useState<'containers' | 'network' | 'api-keys' | 'topology'>('containers');
  
  // ECS management state
  const [ecsDesiredCapacity, setEcsDesiredCapacity] = useState<number>(1);
  const [selectedTask, setSelectedTask] = useState<EcsTaskInfo | null>(null);
  const ecsDeployConfig = {
    clusterName: 'gpu-platform-ecs',
    instanceType: 'g4dn.xlarge',
    maxInstances: 2,
    desiredCount: 0,
    useSpot: true,
    autoLaunchInstances: false,
    autoRunTask: false,
    initialInstanceCount: 1,
  };
  
  // Multi-container management state
  const [showCreateContainer, setShowCreateContainer] = useState(false);
  const [newContainerConfig, setNewContainerConfig] = useState({
    clusterName: '',
    instanceType: 'g4dn.xlarge',
    maxInstances: 2,
    desiredCount: 0,
    useSpot: true,
    autoScaleDownGracePeriodMs: 60000,
    autoScaleDownCheckIntervalMs: 60000,
    // Container / task definition
    dockerImage: '',
    startupCommand: '',
    environmentVariables: '' as string,
    containerPort: 8080,
    hostPort: 0,
    taskCpu: '4096',
    taskMemory: '16384',
    // Health check
    healthCheckPath: '',
    healthCheckIntervalSeconds: 30,
    healthCheckTimeoutSeconds: 5,
    healthCheckRetries: 3,
    // Logging
    logRetentionDays: 30,
    // Platform lifecycle
    idleTimeoutMinutes: 0,
    requireHeartbeat: false,
    maxCostUsd: 0,
    notifyOnComplete: false,
    notifyOnFailure: true,
  });
  const [showAdvancedContainer, setShowAdvancedContainer] = useState(false);
  const [selectedContainerForScaling, setSelectedContainerForScaling] = useState<string | null>(null);
  const [containerScaleValues, setContainerScaleValues] = useState<Record<string, number>>({});
  
  // Instance management state
  const [selectedInstances, setSelectedInstances] = useState<Set<string>>(new Set());
  const [expandedContainer, setExpandedContainer] = useState<string | null>(null);
  const [instanceToDestroy, setInstanceToDestroy] = useState<string | null>(null);
  const [selectedClusterToDelete, setSelectedClusterToDelete] = useState<string | null>(null);
  
  // Task management state for GPU Containers
  const [expandedTasksContainer, setExpandedTasksContainer] = useState<string | null>(null);
  const [selectedClusterForTaskOp, setSelectedClusterForTaskOp] = useState<string | null>(null);
  const [pendingTasksToCleanup, setPendingTasksToCleanup] = useState<EcsTaskInfo[]>([]);
  
  // API Key management state
  const [selectedContainerForApiKey, setSelectedContainerForApiKey] = useState<string | null>(null);
  const [showCreateApiKey, setShowCreateApiKey] = useState(false);
  const [newApiKeyConfig, setNewApiKeyConfig] = useState({
    name: '',
    scopes: ['jobs:read', 'jobs:write'] as ApiKeyScope[],
    rateLimit: 100,
    expiresAt: '',
  });
  
  // EC2 deployment config state
  const ec2DeployConfig = {
    instanceType: 'g4dn.xlarge',
    useSpot: true,
    maxRuntimeHours: 4,
    autoLaunchInstance: false,
  };

  // Fetch deployment status
  const { data: deploymentStatus, isLoading: deploymentsLoading, dataUpdatedAt: deploymentUpdatedAt } = useQuery({
    queryKey: ['deployment-status'],
    queryFn: deploymentApi.getStatus,
    refetchInterval: 5000,
  });

  // Fetch fleet status
  const { data: fleetStatus, isLoading, refetch, dataUpdatedAt: fleetUpdatedAt } = useQuery({
    queryKey: ['fleet-status'],
    queryFn: infrastructureApi.getFleetStatus,
    refetchInterval: 15000,
  });

  // Fetch cost savings estimate
  const { data: costSavings } = useQuery({
    queryKey: ['cost-savings'],
    queryFn: infrastructureApi.getCostSavings,
    refetchInterval: 30000,
  });

  // Fetch ECS cluster details when ECS is deployed
  const { data: ecsClusterDetails, refetch: refetchEcsCluster } = useQuery({
    queryKey: ['ecs-cluster-details', deploymentStatus?.ecs?.ecsClusterName],
    queryFn: () => ecsApi.getClusterDetails(deploymentStatus?.ecs?.ecsClusterName || 'gpu-platform-ecs'),
    enabled: deploymentStatus?.ecs?.status === 'deployed' && !!deploymentStatus.ecs.ecsClusterName,
    refetchInterval: 10000,
  });

  // Fetch all ECS clusters for multi-container management
  const { data: allEcsClusters, refetch: refetchAllClusters, isLoading: isLoadingClusters } = useQuery({
    queryKey: ['all-ecs-clusters'],
    queryFn: ecsApi.listClusters,
    enabled: activeTab === 'gpu-containers',
    refetchInterval: 15000,
  });

  // Fetch API keys
  const { data: apiKeys = [] } = useQuery({
    queryKey: ['api-keys'],
    queryFn: apiKeysApi.list,
    enabled: (activeTab === 'gpu-containers' && gpuContainersSubTab === 'api-keys') || (activeTab === 'deployment' && deploymentSubTab === 'api-keys'),
    refetchInterval: 30000,
  });

  // Fetch GPU jobs for deployment tab (instances and network tabs)
  const { data: gpuJobs = [] } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
    enabled: activeTab === 'deployment' && (deploymentSubTab === 'instances' || deploymentSubTab === 'network'),
    refetchInterval: 10000,
  });

  // Network details are now handled by the NetworkPanel component

  // State for GPU Jobs management
  const [showCreateGpuJob, setShowCreateGpuJob] = useState(false);
  const [selectedInstanceForDetails, setSelectedInstanceForDetails] = useState<string | null>(null);
  const [jobToRemove, setJobToRemove] = useState<string | null>(null);
  const [pendingJobOperations, setPendingJobOperations] = useState<Set<string>>(new Set());

  // Use shared progress tracking
  const { startProgress, completeProgress } = useProgress();

  // Fetch network data for existing network selection in GPU job creation
  const { data: networkData } = useQuery({
    queryKey: ['detailed-network-status'],
    queryFn: detailedNetworkApi.getDetailedStatus,
    enabled: showCreateGpuJob,
    staleTime: 30000,
  });

  // Fetch individual instance details
  const { data: instanceDetails, refetch: refetchInstanceDetails } = useQuery({
    queryKey: ['gpu-job-details', selectedInstanceForDetails],
    queryFn: () => gpuJobsApi.get(selectedInstanceForDetails!),
    enabled: !!selectedInstanceForDetails,
    refetchInterval: 5000,
  });
  // Advanced options toggle
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const [newGpuJobConfig, setNewGpuJobConfig] = useState({
    // Basic Configuration
    instanceType: 'g4dn.xlarge',
    useSpot: true,
    maxRuntimeHours: 4,
    environment: 'development' as 'development' | 'staging' | 'production',
    project: '',
    
    // Storage Configuration
    rootVolumeSize: 100,
    rootVolumeType: 'gp3' as 'gp3' | 'gp2' | 'io1' | 'io2',
    useInstanceStore: false,
    
    // Network Configuration
    assignPublicIp: true,
    networkMode: 'shared' as 'shared' | 'dedicated' | 'existing',
    existingVpcId: '',
    existingSubnetId: '',
    existingSecurityGroupId: '',
    availabilityZone: '',
    
    // Spot Configuration
    maxSpotPriceUsd: undefined as number | undefined,
    spotInterruptionBehavior: 'terminate' as 'terminate' | 'stop' | 'hibernate',
    spotFallbackToOnDemand: false,
    
    // Container Configuration
    dockerImagePreset: '',
    customDockerImage: '',
    startupCommand: '',
    
    // Lifecycle Configuration
    idleTimeoutMinutes: 0,
    requireHeartbeat: false,
    
    // Notifications
    notifyOnComplete: false,
    notifyOnFailure: false,
    
    // Resource Limits
    maxCostUsd: 0,
    
    // Metadata
    name: '',
    selectedTags: {} as Record<string, string>,
    
    // Security
    sshKeyMode: 'generate' as 'generate' | 'none' | 'existing',
    existingSshKeyName: '',
  });

  // Cost estimate query for GPU Instance creation
  const { data: costEstimate, isLoading: estimating } = useQuery({
    queryKey: ['gpu-instance-cost-estimate', newGpuJobConfig.instanceType, newGpuJobConfig.maxRuntimeHours, newGpuJobConfig.useSpot],
    queryFn: () =>
      gpuJobsApi.estimateCost({
        instanceType: newGpuJobConfig.instanceType,
        maxRuntimeHours: newGpuJobConfig.maxRuntimeHours,
        useSpot: newGpuJobConfig.useSpot,
      }),
    enabled: showCreateGpuJob && !!newGpuJobConfig.instanceType && newGpuJobConfig.maxRuntimeHours > 0,
  });
  
  // Pagination state for instances list
  const [instancesPage, setInstancesPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [instancesPerPage, setInstancesPerPage] = useState(5);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Pagination state for API keys (network pagination moved to NetworkPanel)
  const [apiKeysPage, setApiKeysPage] = useState(1);
  const apiKeysPerPage = 10;

  // Pagination state for GPU Containers (ECS clusters)
  const [containersPage, setContainersPage] = useState(1);
  const [containersPerPage, setContainersPerPage] = useState(5);
  const [containersDateFrom, setContainersDateFrom] = useState<string>('');
  const [containersDateTo, setContainersDateTo] = useState<string>('');

  // State for API Key management for GPU Instances
  const [selectedGpuInstanceForApiKey, setSelectedGpuInstanceForApiKey] = useState<string | null>(null);
  const [showCreateGpuApiKey, setShowCreateGpuApiKey] = useState(false);
  const [newGpuApiKeyConfig, setNewGpuApiKeyConfig] = useState({
    name: '',
    scopes: ['jobs:read', 'jobs:write'] as ApiKeyScope[],
    rateLimit: 100,
    expiresAt: '',
  });

  // Refresh data when page is visited (mount or becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Refresh all data when page becomes visible
        refetch();
        queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
        queryClient.invalidateQueries({ queryKey: ['cost-savings'] });
        queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      }
    };

    // Refresh on mount
    refetch();
    queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
    queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
    queryClient.invalidateQueries({ queryKey: ['cost-savings'] });
    queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });

    // Refresh when page becomes visible
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch, queryClient]);

  // Refresh data when tab changes
  useEffect(() => {
    if (activeTab === 'deployment') {
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
    } else if (activeTab === 'fleet') {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['cost-savings'] });
    } else if (activeTab === 'gpu-containers') {
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
    }
  }, [activeTab, queryClient, refetch]);

  // Deployment mutations
  const deployEc2Mutation = useMutation({
    mutationFn: async () => {
      const result = await deploymentApi.deployEc2({ 
        region: 'us-east-1',
        instanceTypes: [ec2DeployConfig.instanceType],
        useSpot: ec2DeployConfig.useSpot,
      });
      return result;
    },
    onSuccess: async (result: DeploymentResult) => {
      let message = result.message;
      
      if (ec2DeployConfig.autoLaunchInstance) {
        message = `${result.message} - Launching GPU instance...`;
        toast.success(message);
        
        // Poll for deployment completion and then launch instance
        const pollForDeployment = async () => {
          let attempts = 0;
          const maxAttempts = 60; // 5 minutes max (5 sec intervals)
          
          const checkAndLaunch = async () => {
            attempts++;
            try {
              const status = await deploymentApi.getStatus();
              if (status.ec2?.status === 'deployed') {
                // Deployment complete, launch instance
                try {
                  const jobResult = await gpuJobsApi.create({
                    instanceType: ec2DeployConfig.instanceType,
                    useSpot: ec2DeployConfig.useSpot,
                    maxRuntimeHours: ec2DeployConfig.maxRuntimeHours,
                    environment: 'development',
                  });
                  toast.success(`✅ GPU instance launched! Job ID: ${jobResult.jobId}`);
                  queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
                  queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
                } catch (jobError) {
                  toast.error(`Infrastructure deployed but instance launch failed: ${(jobError as Error).message}`);
                }
                return;
              } else if (status.ec2?.status === 'failed') {
                toast.error('Infrastructure deployment failed');
                return;
              }
              
              // Still deploying, continue polling
              if (attempts < maxAttempts) {
                setTimeout(checkAndLaunch, 5000);
              } else {
                toast.error('Deployment is taking too long. Check the Infrastructure page for status.');
              }
            } catch {
              if (attempts < maxAttempts) {
                setTimeout(checkAndLaunch, 5000);
              }
            }
          };
          
          // Start polling after a short delay
          setTimeout(checkAndLaunch, 3000);
        };
        
        pollForDeployment();
      } else {
        toast.success(message);
      }
      
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to deploy EC2: ${error.message}`);
    },
  });

  const deployEcsMutation = useMutation({
    mutationFn: async (config?: {
      clusterName: string;
      instanceType: string;
      maxInstances: number;
      desiredCount: number;
      useSpot: boolean;
      autoScaleDownGracePeriodMs?: number;
      autoScaleDownCheckIntervalMs?: number;
      dockerImage?: string;
      startupCommand?: string;
      environmentVariables?: Record<string, string>;
      containerPort?: number;
      hostPort?: number;
      taskCpu?: string;
      taskMemory?: string;
      healthCheckPath?: string;
      healthCheckIntervalSeconds?: number;
      healthCheckTimeoutSeconds?: number;
      healthCheckRetries?: number;
      logRetentionDays?: number;
      idleTimeoutMinutes?: number;
      requireHeartbeat?: boolean;
      maxCostUsd?: number;
      notifyOnComplete?: boolean;
      notifyOnFailure?: boolean;
    }) => {
      const deployConfig = config || {
        clusterName: ecsDeployConfig.clusterName,
        instanceType: ecsDeployConfig.instanceType,
        maxInstances: ecsDeployConfig.maxInstances,
        desiredCount: ecsDeployConfig.autoLaunchInstances ? ecsDeployConfig.initialInstanceCount : 0,
        useSpot: ecsDeployConfig.useSpot,
      };

      const networkToastId = toast.loading(
        `🌐 Deploying network infrastructure for ${deployConfig.clusterName}...`,
        { duration: Infinity }
      );

      try {
        const result = await deploymentApi.deployEcs({
          clusterName: deployConfig.clusterName,
          instanceTypes: [deployConfig.instanceType],
          maxInstances: deployConfig.maxInstances,
          desiredCount: deployConfig.desiredCount,
          useSpot: deployConfig.useSpot,
          autoScaleDownGracePeriodMs: deployConfig.autoScaleDownGracePeriodMs,
          autoScaleDownCheckIntervalMs: deployConfig.autoScaleDownCheckIntervalMs,
          ...(deployConfig.dockerImage && { dockerImage: deployConfig.dockerImage }),
          ...(deployConfig.startupCommand && { startupCommand: deployConfig.startupCommand }),
          ...(deployConfig.environmentVariables && Object.keys(deployConfig.environmentVariables).length > 0 && { environmentVariables: deployConfig.environmentVariables }),
          ...(deployConfig.containerPort && { containerPort: deployConfig.containerPort }),
          ...(deployConfig.hostPort && { hostPort: deployConfig.hostPort }),
          ...(deployConfig.taskCpu && { taskCpu: deployConfig.taskCpu }),
          ...(deployConfig.taskMemory && { taskMemory: deployConfig.taskMemory }),
          ...(deployConfig.healthCheckPath && { healthCheckPath: deployConfig.healthCheckPath }),
          ...(deployConfig.healthCheckIntervalSeconds && { healthCheckIntervalSeconds: deployConfig.healthCheckIntervalSeconds }),
          ...(deployConfig.healthCheckTimeoutSeconds && { healthCheckTimeoutSeconds: deployConfig.healthCheckTimeoutSeconds }),
          ...(deployConfig.healthCheckRetries && { healthCheckRetries: deployConfig.healthCheckRetries }),
          ...(deployConfig.logRetentionDays && { logRetentionDays: deployConfig.logRetentionDays }),
          ...(deployConfig.idleTimeoutMinutes && { idleTimeoutMinutes: deployConfig.idleTimeoutMinutes }),
          ...(deployConfig.requireHeartbeat && { requireHeartbeat: deployConfig.requireHeartbeat }),
          ...(deployConfig.maxCostUsd && { maxCostUsd: deployConfig.maxCostUsd }),
          ...(deployConfig.notifyOnComplete && { notifyOnComplete: deployConfig.notifyOnComplete }),
          ...(deployConfig.notifyOnFailure !== undefined && { notifyOnFailure: deployConfig.notifyOnFailure }),
        });
        
        // Dismiss network deployment toast
        toast.dismiss(networkToastId);
        
        // Show success toast for network
        toast.success(
          `✅ Network infrastructure ready for ${deployConfig.clusterName}`,
          { duration: 3000 }
        );

        return { result, config: deployConfig };
      } catch (error) {
        toast.dismiss(networkToastId);
        throw error;
      }
    },
    onSuccess: ({ result, config }) => {
      let message = result.message;
      
      const isAutoLaunching = config.desiredCount > 0;
      
      if (isAutoLaunching) {
        message = `${result.message} - Launching ${config.desiredCount} GPU instance(s)...`;
        
        // If auto-run task is enabled for default config
        if (config.clusterName === ecsDeployConfig.clusterName && ecsDeployConfig.autoRunTask) {
          toast.success(message);
          toast.loading(
            `GPU instances starting (2-5 min). Tasks will run automatically once instances register.`,
            { duration: 10000, icon: '🚀' }
          );
          
          // Poll for instances and run task when ready
          const pollInterval = setInterval(async () => {
            try {
              const clusterDetails = await ecsApi.getClusterDetails(config.clusterName);
              if (clusterDetails && clusterDetails.registeredContainerInstancesCount > 0) {
                clearInterval(pollInterval);
                try {
                  await ecsApi.runTask(config.clusterName);
                  toast.success('✅ GPU instance ready - task started automatically!');
                  queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
                } catch (taskError) {
                  toast.error('Instance ready but task failed to start. Use "Run Task" button.');
                }
              }
            } catch {
              // Cluster not ready yet, continue polling
            }
          }, 15000); // Poll every 15 seconds
          
          // Stop polling after 10 minutes max
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 600000);
        } else {
          toast.success(message);
        }
      } else {
        toast.success(message);
      }
      
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      queryClient.invalidateQueries({ queryKey: ['all-ecs-clusters'] });
      queryClient.invalidateQueries({ queryKey: ['operations-active'] });
      setConfirmAction(null);
      setShowCreateContainer(false);
      
      // Show operation tracking info if returned
      if (result.operationId) {
        toast(
          `Operation ${result.operationId.slice(0, 8)}... tracking in Operations panel.`,
          { icon: '📋', duration: 4000 }
        );
      }
      
      // Reset the form
      setNewContainerConfig({
        clusterName: '',
        instanceType: 'g4dn.xlarge',
        maxInstances: 2,
        desiredCount: 0,
        useSpot: true,
        autoScaleDownGracePeriodMs: 60000,
        autoScaleDownCheckIntervalMs: 60000,
        dockerImage: '',
        startupCommand: '',
        environmentVariables: '' as string,
        containerPort: 8080,
        hostPort: 0,
        taskCpu: '4096',
        taskMemory: '16384',
        healthCheckPath: '',
        healthCheckIntervalSeconds: 30,
        healthCheckTimeoutSeconds: 5,
        healthCheckRetries: 3,
        logRetentionDays: 30,
        idleTimeoutMinutes: 0,
        requireHeartbeat: false,
        maxCostUsd: 0,
        notifyOnComplete: false,
        notifyOnFailure: true,
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to deploy GPU cluster: ${error.message}`);
      queryClient.invalidateQueries({ queryKey: ['operations-active'] });
    },
  });

  // ECS scaling mutation
  const scaleEcsMutation = useMutation({
    mutationFn: (desiredCapacity: number) => 
      ecsApi.scaleCluster(deploymentStatus?.ecs?.ecsClusterName || 'gpu-platform-ecs', desiredCapacity),
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Unknown error';
      // Check if this is an ASG not found error
      if (errorMessage.includes('Auto Scaling Group') && errorMessage.includes('not found')) {
        toast.error(
          <div>
            <div className="font-semibold mb-1">Infrastructure Incomplete</div>
            <div className="text-sm">{errorMessage}</div>
            <button
              onClick={() => setConfirmAction('repair-ecs')}
              className="mt-2 px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Repair Infrastructure
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(`Failed to scale cluster: ${errorMessage}`);
      }
    },
  });

  // ECS repair mutation
  const repairEcsMutation = useMutation({
    mutationFn: () => 
      ecsApi.repairCluster(deploymentStatus?.ecs?.ecsClusterName || 'gpu-platform-ecs', ecsDeployConfig.instanceType),
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to repair cluster: ${error.message}`);
    },
  });

  // Run ECS task mutation - accepts cluster name
  const runEcsTaskMutation = useMutation({
    mutationFn: (clusterName?: string) => 
      ecsApi.runTask(clusterName || deploymentStatus?.ecs?.ecsClusterName || 'gpu-platform-ecs'),
    onSuccess: (result, clusterName) => {
      toast.success(result.message);
      if (clusterName) {
        queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details', clusterName] });
      }
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      queryClient.invalidateQueries({ queryKey: ['all-ecs-clusters'] });
      setSelectedClusterForTaskOp(null);
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      if (error.message.includes('No tasks were started')) {
        toast.error('Task could not be started. The instances may still be initializing. Please wait a moment and try again.');
      } else {
        toast.error(`Failed to run task: ${error.message}`);
      }
      setConfirmAction(null);
    },
  });

  // Stop ECS task mutation - accepts cluster name and task
  const stopEcsTaskMutation = useMutation({
    mutationFn: ({ clusterName, task }: { clusterName: string; task: EcsTaskInfo }) => 
      ecsApi.stopTask(clusterName, task.taskArn),
    onSuccess: (result, { clusterName }) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details', clusterName] });
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      setSelectedTask(null);
      setSelectedClusterForTaskOp(null);
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to stop task: ${error.message}`);
    },
  });

  // Clean up all stuck/pending tasks mutation - accepts cluster name and tasks
  const cleanupStuckTasksMutation = useMutation({
    mutationFn: async ({ clusterName, tasks }: { clusterName: string; tasks: EcsTaskInfo[] }) => {
      const results = await Promise.allSettled(
        tasks.map(task => ecsApi.stopTask(clusterName, task.taskArn, 'Cleaning up stuck task'))
      );

      const stopped = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { stopped, failed, total: tasks.length, clusterName };
    },
    onSuccess: (result) => {
      if (result.stopped > 0) {
        toast.success(`Successfully stopped ${result.stopped} stuck task(s)`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to stop ${result.failed} task(s)`);
      }
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details', result.clusterName] });
      queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
      setSelectedClusterForTaskOp(null);
      setPendingTasksToCleanup([]);
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to cleanup stuck tasks: ${error.message}`);
    },
  });

  const destroyEc2Mutation = useMutation({
    mutationFn: deploymentApi.destroyEc2,
    onSuccess: (result: DeploymentResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to destroy EC2: ${error.message}`);
    },
  });

  const destroyEcsMutation = useMutation({
    mutationFn: deploymentApi.destroyEcs,
    onSuccess: (result: DeploymentResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to destroy ECS: ${error.message}`);
    },
  });

  // Cancel deployment mutations
  const cancelEc2Mutation = useMutation({
    mutationFn: deploymentApi.cancelEc2,
    onSuccess: (result: DeploymentResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel EC2 deployment: ${error.message}`);
    },
  });

  const cancelEcsMutation = useMutation({
    mutationFn: deploymentApi.cancelEcs,
    onSuccess: (result: DeploymentResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel ECS deployment: ${error.message}`);
    },
  });

  // Fleet action mutations
  const startFleetMutation = useMutation({
    mutationFn: infrastructureApi.startFleet,
    onSuccess: (result: FleetActionResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to start fleet: ${error.message}`);
    },
  });

  const stopFleetMutation = useMutation({
    mutationFn: infrastructureApi.stopFleet,
    onSuccess: (result: FleetActionResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to stop fleet: ${error.message}`);
    },
  });

  const terminateFleetMutation = useMutation({
    mutationFn: infrastructureApi.terminateFleet,
    onSuccess: (result: FleetActionResult) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to terminate fleet: ${error.message}`);
    },
  });

  // Individual instance mutations
  const startInstanceMutation = useMutation({
    mutationFn: infrastructureApi.startInstance,
    onMutate: async (instanceId) => {
      // Find the job and mark as pending to prevent duplicate operations
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      if (job) {
        setPendingJobOperations(prev => new Set(prev).add(job.id));
      }
      
      // Optimistically update GPU jobs to show 'provisioning' status
      await queryClient.cancelQueries({ queryKey: ['gpu-jobs'] });
      const previousJobs = queryClient.getQueryData(['gpu-jobs']);
      queryClient.setQueryData(['gpu-jobs'], (old: any) => 
        old?.map((job: any) => 
          job.instanceId === instanceId ? { ...job, status: 'provisioning' } : job
        )
      );
      return { previousJobs, jobId: job?.id };
    },
    onSuccess: (result, _instanceId, context) => {
      toast.success(result.message + ' Refreshing status...');
      // Refetch after a short delay to get the actual state from AWS
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      }, 2000);
      
      // Clear pending state
      if (context?.jobId) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(context.jobId!);
          return next;
        });
      }
    },
    onError: (error: Error, _instanceId, context) => {
      // Rollback on error
      if (context?.previousJobs) {
        queryClient.setQueryData(['gpu-jobs'], context.previousJobs);
      }
      const msg = error.message || 'Unknown error';
      toast.error(`Failed to start instance: ${msg}`);
      
      // Clear pending state
      if (context?.jobId) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(context.jobId!);
          return next;
        });
      }
    },
  });

  const stopInstanceMutation = useMutation({
    mutationFn: infrastructureApi.stopInstance,
    onMutate: async (instanceId) => {
      // Find the job and mark as pending to prevent duplicate operations
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      if (job) {
        setPendingJobOperations(prev => new Set(prev).add(job.id));
      }
      
      // Optimistically update GPU jobs to show 'stopping' status
      await queryClient.cancelQueries({ queryKey: ['gpu-jobs'] });
      const previousJobs = queryClient.getQueryData(['gpu-jobs']);
      queryClient.setQueryData(['gpu-jobs'], (old: any) => 
        old?.map((job: any) => 
          job.instanceId === instanceId ? { ...job, status: 'stopping' } : job
        )
      );
      return { previousJobs, jobId: job?.id };
    },
    onSuccess: (result, _instanceId, context) => {
      toast.success(result.message + ' Refreshing status...');
      // Refetch after a short delay to get the actual state from AWS
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      }, 2000);
      
      // Clear pending state
      if (context?.jobId) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(context.jobId!);
          return next;
        });
      }
    },
    onError: (error: Error, _instanceId, context) => {
      // Rollback on error
      if (context?.previousJobs) {
        queryClient.setQueryData(['gpu-jobs'], context.previousJobs);
      }
      const msg = error.message || 'Unknown error';
      // Check for spot instance issues
      if (msg.includes('spot') || msg.includes('Spot')) {
        toast.error('Cannot stop Spot instances. Use Terminate instead.');
      } else {
        toast.error(`Failed to stop instance: ${msg}`);
      }
      
      // Clear pending state
      if (context?.jobId) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(context.jobId!);
          return next;
        });
      }
    },
  });

  const hibernateInstanceMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      // Find the job and mark as pending to prevent duplicate operations
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      if (job) {
        setPendingJobOperations(prev => new Set(prev).add(job.id));
      }
      return infrastructureApi.hibernateInstance(instanceId);
    },
    onSuccess: (result, instanceId) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      
      // Clear pending state
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
    onError: (error: Error, instanceId) => {
      const msg = error.message || 'Unknown error';
      // Check for hibernation not supported
      if (msg.includes('hibernat') || msg.includes('Hibernat')) {
        toast.error('This instance does not support hibernation. Use Stop instead.');
      } else {
        toast.error(`Failed to hibernate instance: ${msg}`);
      }
      
      // Clear pending state
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
  });

  const terminateInstanceMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      // Find the GPU job by instanceId
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      
      if (job) {
        // Mark job as pending to prevent duplicate operations
        setPendingJobOperations(prev => new Set(prev).add(job.id));
        
        // Start progress with initial resources for AWS-validated tracking
        startProgress(job.id, {
          vpcId: job.vpcId,
          subnetId: job.subnetId,
          securityGroupId: job.securityGroupId,
          instanceId: job.instanceId,
        });
        
        // If job is running/pending, cancel it first (terminates instance + cleans network)
        if (['pending', 'provisioning', 'running'].includes(job.status)) {
           return gpuJobsApi.cancel(job.id);
        } 
        // If job is in terminal state, remove it (cleans network + deletes record)
        else if (['completed', 'failed', 'cancelled', 'terminated'].includes(job.status)) {
           return gpuJobsApi.remove(job.id);
        }
        // For other states (e.g. 'stopping'), fall back to infrastructure API
        else {
           // We use infrastructure API but we want to track progress against the job ID if possible
           // However, infrastructureApi returns a different shape, handled in onSuccess
           return infrastructureApi.terminateInstance(instanceId);
        }
      } else {
        // Fallback to infrastructure API if no matching GPU job found (no resources to track)
        startProgress(instanceId);
        return infrastructureApi.terminateInstance(instanceId);
      }
    },
    onSuccess: (data: any, instanceId) => {
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      const progressId = job?.id || instanceId;

      // Check if response is from gpuJobsApi.remove (has message property and NO networkCleanup)
      // or from infrastructureApi.terminateInstance / gpuJobsApi.cancel (has networkCleanup)
      const isRemoveResponse = !('networkCleanup' in data) && 'message' in data;
      
      if (isRemoveResponse) {
        // Logic for gpuJobsApi.remove (purge)
        const networkCleaned = data.message?.includes('network');
        
        completeProgress(
          progressId,
          true,
          networkCleaned 
            ? 'All network resources have been cleaned up'
            : 'Instance removed, network preserved',
          !networkCleaned
        );

        if (networkCleaned) {
          toast.success(
            <div>
              <p className="font-medium">Network destroyed successfully</p>
              <p className="text-sm opacity-80 mt-1">All associated resources have been cleaned up</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.success('Instance removed successfully (network preserved)');
        }
      } else {
        // Logic for infrastructureApi.terminateInstance OR gpuJobsApi.cancel
        // Both return a structure with networkCleanup object (if cancel is successful)
        const result = data;
        
        if (result.networkCleanup?.attempted) {
          if (result.networkCleanup.sharedWithOtherInstances) {
            completeProgress(
              progressId,
              true,
              'Network preserved (shared with other instances)',
              true
            );
            toast.success(
              <div>
                <p className="font-medium">Instance terminated</p>
                <p className="text-sm opacity-80 mt-1">Network preserved (shared with other instances)</p>
              </div>,
              { duration: 5000 }
            );
          } else if (result.networkCleanup.success) {
            completeProgress(
              progressId,
              true,
              `Network cleaned up (VPC ${result.networkCleanup.vpcId?.slice(0, 12)}...)`,
              false
            );
            toast.success(
              <div>
                <p className="font-medium">Instance terminated</p>
                <p className="text-sm opacity-80 mt-1">Network automatically cleaned up (VPC {result.networkCleanup.vpcId?.slice(0, 12)}...)</p>
              </div>,
              { duration: 5000 }
            );
          } else {
            completeProgress(
              progressId,
              false,
              result.networkCleanup.message || 'Network cleanup attempted but may have issues',
              true
            );
            toast.success(
              <div>
                <p className="font-medium">Instance terminated</p>
                <p className="text-sm text-yellow-400 mt-1">Network cleanup attempted but may have issues</p>
              </div>,
              { duration: 5000 }
            );
          }
        } else {
          // Fallback if no network cleanup info (e.g. simple terminate)
          completeProgress(progressId, true, 'Instance terminated');
          toast.success(result.message || 'Instance terminated');
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
      
      // Clear pending state
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
    onError: (error: Error, instanceId) => {
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      const progressId = matchedJob?.id || instanceId;
      
      completeProgress(progressId, false, `Failed: ${error.message}`);
      const msg = error.message || 'Unknown error';
      toast.error(
        <div>
          <p className="font-medium">Failed to terminate instance</p>
          <p className="text-sm opacity-80 mt-1">{msg}</p>
        </div>,
        { duration: 6000 }
      );
      
      // Clear pending state
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
  });

  // API Key mutations
  const createApiKeyMutation = useMutation({
    mutationFn: apiKeysApi.create,
    onSuccess: (result: NewApiKey) => {
      toast.success('API key created successfully');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setShowCreateApiKey(false);
      setNewApiKeyConfig({
        name: '',
        scopes: ['jobs:read', 'jobs:write'],
        rateLimit: 100,
        expiresAt: '',
      });
      // Show the full key in a modal or alert (only shown once)
      toast(() => (
        <div className="flex flex-col gap-2">
          <p className="font-semibold">API Key Created!</p>
          <p className="text-xs text-surface-400">Save this key - it won't be shown again</p>
          <div className="p-2 bg-surface-800 rounded font-mono text-xs break-all">
            {result.key}
          </div>
        </div>
      ), { duration: 10000 });
    },
    onError: (error: Error) => {
      toast.error(`Failed to create API key: ${error.message}`);
    },
  });

  const revokeApiKeyMutation = useMutation({
    mutationFn: apiKeysApi.revoke,
    onSuccess: () => {
      toast.success('API key revoked successfully');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to revoke API key: ${error.message}`);
    },
  });

  const deleteApiKeyMutation = useMutation({
    mutationFn: apiKeysApi.delete,
    onSuccess: () => {
      toast.success('API key deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete API key: ${error.message}`);
    },
  });

  // GPU Instance mutations
  const createGpuJobMutation = useMutation({
    mutationFn: async (jobConfig: any) => {
      // Show instance creation toast - infrastructure is now auto-created in the backend
      const createToastId = toast.loading(
        '🚀 Creating GPU instance (network infrastructure will be auto-created if needed)...',
        { duration: Infinity }
      );
      
      try {
        const result = await gpuJobsApi.create(jobConfig);
        toast.dismiss(createToastId);
        return result;
      } catch (error) {
        toast.dismiss(createToastId);
        throw error;
      }
    },
    onSuccess: (result) => {
      toast.success(`GPU instance created successfully! Instance ID: ${result.jobId}`);
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      setShowCreateGpuJob(false);
      setShowAdvanced(false);
      setNewGpuJobConfig({
        instanceType: 'g4dn.xlarge',
        useSpot: true,
        maxRuntimeHours: 4,
        environment: 'development',
        project: '',
        rootVolumeSize: 100,
        rootVolumeType: 'gp3',
        useInstanceStore: false,
        assignPublicIp: true,
        networkMode: 'shared',
        existingVpcId: '',
        existingSubnetId: '',
        existingSecurityGroupId: '',
        availabilityZone: '',
        maxSpotPriceUsd: undefined,
        spotInterruptionBehavior: 'terminate',
        spotFallbackToOnDemand: false,
        dockerImagePreset: '',
        customDockerImage: '',
        startupCommand: '',
        idleTimeoutMinutes: 0,
        requireHeartbeat: false,
        notifyOnComplete: false,
        notifyOnFailure: false,
        maxCostUsd: 0,
        name: '',
        selectedTags: {},
        sshKeyMode: 'generate',
        existingSshKeyName: '',
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to create GPU instance: ${error.message}`);
    },
  });

  const cancelGpuJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      setPendingJobOperations(prev => new Set(prev).add(jobId));
      
      // Get job details for AWS-validated progress tracking
      const job = gpuJobs.find(j => j.id === jobId);
      
      // Start progress with initial resources for AWS validation
      // The polling will continue even if the API call times out
      startProgress(jobId, {
        vpcId: job?.vpcId,
        subnetId: job?.subnetId,
        securityGroupId: job?.securityGroupId,
        instanceId: job?.instanceId,
      });
      
      try {
        return await gpuJobsApi.cancel(jobId);
      } catch (error: any) {
        // If timeout, don't fail - let the AWS polling continue
        if (error.message?.includes('timeout')) {
          toast(
            <div>
              <p className="font-medium">Cancellation in progress</p>
              <p className="text-sm opacity-80 mt-1">Backend timed out, tracking via AWS polling</p>
            </div>,
            { duration: 4000, icon: '⏳' }
          );
          return { 
            id: jobId, 
            status: 'cancelled' as const,
            networkCleanup: { attempted: true, success: false, message: 'Polling AWS for status...' } 
          } as any;
        }
        throw error;
      }
    },
    onSuccess: (data, jobId) => {
      // If this was a timeout response, progress polling will continue
      if (data.networkCleanup?.message?.includes('Polling AWS')) {
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
        return;
      }
      
      // Check for network cleanup info
      const networkCleanup = data.networkCleanup;
      
      if (networkCleanup?.attempted) {
        if (networkCleanup.sharedWithOtherInstances) {
          completeProgress(
            jobId, 
            true, 
            'Network preserved (shared with other instances)',
            true
          );
          toast.success(
            <div>
              <p className="font-medium">Instance cancelled successfully</p>
              <p className="text-sm opacity-80 mt-1">Network preserved (shared with other instances)</p>
            </div>,
            { duration: 5000 }
          );
        } else if (networkCleanup.success) {
          completeProgress(
            jobId, 
            true, 
            `Network cleaned up (VPC ${networkCleanup.vpcId?.slice(0, 12)}...)`,
            false
          );
          toast.success(
            <div>
              <p className="font-medium">Instance cancelled successfully</p>
              <p className="text-sm opacity-80 mt-1">Network automatically cleaned up. Check Network page to confirm.</p>
            </div>,
            { duration: 6000 }
          );
        } else {
          completeProgress(
            jobId, 
            false, 
            networkCleanup.message || 'Network cleanup failed',
            true
          );
          toast(
            <div>
              <p className="font-medium text-yellow-300">Instance cancelled but network cleanup failed</p>
              <p className="text-sm opacity-80 mt-1">{networkCleanup.message}</p>
              <p className="text-xs opacity-70 mt-2">You may need to manually remove the network from the Network page.</p>
            </div>,
            { duration: 8000, icon: '⚠️' }
          );
        }
      } else {
        completeProgress(jobId, true, 'Instance cancelled successfully');
        toast.success('GPU instance terminated successfully');
      }
      
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['gpu-job-details', jobId] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
      setPendingJobOperations(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    },
    onError: (error: Error, jobId) => {
      // Don't complete progress on timeout - let AWS polling continue
      if (!error.message?.includes('timeout')) {
        completeProgress(jobId, false, `Failed: ${error.message}`);
      }
      
      toast.error(
        <div>
          <p className="font-medium">{error.message?.includes('timeout') ? 'API timeout' : 'Failed to cancel instance'}</p>
          <p className="text-sm opacity-80 mt-1">{error.message}</p>
          {error.message?.includes('timeout') && (
            <p className="text-xs opacity-70 mt-2">Still tracking progress via AWS polling...</p>
          )}
        </div>,
        { duration: 6000 }
      );
      setPendingJobOperations(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
    },
  });

  const removeGpuJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      setPendingJobOperations(prev => new Set(prev).add(jobId));
      
      // Get job details for AWS-validated progress tracking
      const job = gpuJobs.find(j => j.id === jobId);
      
      // Start progress with initial resources for AWS validation
      // The polling will continue even if the API call times out
      startProgress(jobId, {
        vpcId: job?.vpcId,
        subnetId: job?.subnetId,
        securityGroupId: job?.securityGroupId,
        instanceId: job?.instanceId,
      });
      
      try {
        return await gpuJobsApi.remove(jobId);
      } catch (error: any) {
        // If timeout, don't fail - let the AWS polling continue
        if (error.message?.includes('timeout')) {
          toast(
            <div>
              <p className="font-medium">Removal in progress</p>
              <p className="text-sm opacity-80 mt-1">Backend timed out, tracking via AWS polling</p>
            </div>,
            { duration: 4000, icon: '⏳' }
          );
          return { 
            deleted: true, 
            message: 'Removal initiated - polling AWS for completion' 
          };
        }
        throw error;
      }
    },
    onSuccess: (data, jobId) => {
      // If this was a timeout response, progress polling will continue
      if (data.message?.includes('polling AWS')) {
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
        setJobToRemove(null);
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
        return;
      }
      
      // Check for network cleanup info
      const networkCleaned = data.message.includes('network');
      
      // DON'T complete progress here for network cleanup - let AWS polling determine when it's truly done
      // The ProgressContext will automatically complete when VPC is verified as deleted
      
      if (networkCleaned) {
        // Show informative toast but don't mark as complete yet - let AWS polling verify
        toast(
          <div>
            <p className="font-medium">Network cleanup initiated</p>
            <p className="text-sm opacity-80 mt-1">Removing VPC, security groups, subnets...</p>
            <p className="text-xs opacity-60 mt-1">Progress tracked via AWS polling</p>
          </div>,
          { duration: 4000, icon: '🔄' }
        );
      } else {
        // Network preserved - can complete immediately
        completeProgress(
          jobId,
          true,
          'Instance removed (network preserved)',
          true
        );
        toast.success(
          <div>
            <p className="font-medium">GPU instance removed</p>
            <p className="text-sm opacity-80 mt-1">Network resources preserved for reuse</p>
          </div>
        );
      }
      
      // Invalidate all relevant queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
      queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
      queryClient.invalidateQueries({ queryKey: ['running-instances'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
      setJobToRemove(null);
      setSelectedInstanceForDetails(null);
      setPendingJobOperations(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    },
    onError: (error: Error, jobId) => {
      // Don't complete progress on timeout - let AWS polling continue
      if (!error.message?.includes('timeout')) {
        completeProgress(jobId, false, `Failed: ${error.message}`);
      }
      
      toast.error(
        <div>
          <p className="font-medium">{error.message?.includes('timeout') ? 'API timeout' : 'Failed to remove GPU instance'}</p>
          <p className="text-sm opacity-80 mt-1">{error.message}</p>
          {error.message?.includes('timeout') && (
            <p className="text-xs opacity-70 mt-2">Still tracking progress via AWS polling...</p>
          )}
        </div>,
        { duration: 6000 }
      );
      setJobToRemove(null);
      setPendingJobOperations(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
    },
  });

  // ECS Container Instance termination mutations - uses gpuJobsApi.remove like NetworkPage
  const terminateEcsInstanceMutation = useMutation({
    mutationFn: async ({ clusterName, instanceId }: { clusterName: string; instanceId: string }) => {
      // Find the GPU job by instanceId
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      if (job) {
        // Mark job as pending to prevent duplicate operations
        setPendingJobOperations(prev => new Set(prev).add(job.id));
        
        // Start progress with initial resources for AWS-validated tracking
        startProgress(job.id, {
          vpcId: job.vpcId,
          subnetId: job.subnetId,
          securityGroupId: job.securityGroupId,
          instanceId: job.instanceId,
        });
        return gpuJobsApi.remove(job.id);
      } else {
        // Fallback to ECS API if no matching GPU job found (no resources to track)
        startProgress(instanceId);
        return ecsApi.terminateInstance(clusterName, instanceId);
      }
    },
    onSuccess: (data, { instanceId }) => {
      // Find the GPU job to get the jobId for progress tracking
      const job = gpuJobs.find(j => j.instanceId === instanceId);
      const progressId = job?.id || instanceId;
      
      // Check if response is from gpuJobsApi.remove (has message property)
      if ('message' in data && typeof data.message === 'string') {
        const networkCleaned = data.message.includes('network');
        
        completeProgress(
          progressId,
          true,
          networkCleaned 
            ? 'All network resources have been cleaned up'
            : 'Instance removed, network preserved',
          !networkCleaned
        );

        if (networkCleaned) {
          toast.success(
            <div>
              <p className="font-medium">Network destroyed successfully</p>
              <p className="text-sm opacity-80 mt-1">All associated resources have been cleaned up</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.success('Instance removed successfully (network preserved)');
        }
      } else {
        // Handle ECS API response (fallback)
        const result = data as { networkCleanup?: { attempted?: boolean; sharedWithOtherInstances?: boolean; success?: boolean; vpcId?: string; message?: string }; message?: string };
        if (result.networkCleanup?.attempted) {
          if (result.networkCleanup.sharedWithOtherInstances) {
            completeProgress(
              progressId,
              true,
              'Network preserved (shared with other instances)',
              true
            );
            toast.success(
              <div>
                <p className="font-medium">Container instance terminated</p>
                <p className="text-sm opacity-80 mt-1">Network preserved (shared with other instances)</p>
              </div>,
              { duration: 5000 }
            );
          } else if (result.networkCleanup.success) {
            completeProgress(
              progressId,
              true,
              `Network cleaned up (VPC ${result.networkCleanup.vpcId?.slice(0, 12)}...)`,
              false
            );
            toast.success(
              <div>
                <p className="font-medium">Container instance terminated</p>
                <p className="text-sm opacity-80 mt-1">Network automatically cleaned up (VPC {result.networkCleanup.vpcId?.slice(0, 12)}...)</p>
              </div>,
              { duration: 5000 }
            );
          } else {
            completeProgress(
              progressId,
              false,
              result.networkCleanup.message || 'Network cleanup attempted but may have issues',
              true
            );
            toast.success(
              <div>
                <p className="font-medium">Container instance terminated</p>
                <p className="text-sm text-yellow-400 mt-1">Network cleanup attempted but may have issues</p>
              </div>,
              { duration: 5000 }
            );
          }
        } else {
          completeProgress(progressId, true, 'Container instance terminated');
          toast.success(result.message || 'Instance terminated');
        }
      }
      
      refetchAllClusters();
      setInstanceToDestroy(null);
      setConfirmAction(null);
      // Invalidate all relevant queries like NetworkPage does
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
      
      // Clear pending state
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
    onError: (error: Error, { instanceId }) => {
      const matchedJob = gpuJobs.find(j => j.instanceId === instanceId);
      const progressId = matchedJob?.id || instanceId;
      completeProgress(progressId, false, `Failed: ${error.message}`);
      toast.error(`Failed to destroy network: ${error.message}`);
      
      // Clear pending state
      if (matchedJob) {
        setPendingJobOperations(prev => {
          const next = new Set(prev);
          next.delete(matchedJob.id);
          return next;
        });
      }
    },
  });

  const terminateBulkInstancesMutation = useMutation({
    mutationFn: ({ clusterName, instanceIds }: { clusterName: string; instanceIds: string[] }) =>
      ecsApi.terminateInstances(clusterName, instanceIds),
    onSuccess: (result) => {
      if (result.terminated.length > 0) {
        toast.success(`Successfully terminated ${result.terminated.length} instance(s)`);
      }
      if (result.failed.length > 0) {
        toast.error(`Failed to terminate ${result.failed.length} instance(s)`);
      }
      refetchAllClusters();
      setSelectedInstances(new Set());
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to terminate instances: ${error.message}`);
    },
  });

  const deleteEcsClusterMutation = useMutation({
    mutationFn: (clusterName: string) => ecsApi.deleteCluster(clusterName),
    onSuccess: (result) => {
      // CRITICAL FIX: This is now an async operation - show tracking info
      toast.success(
        result.message || 'Cluster deletion initiated. Track progress in Operations.',
        { duration: 5000 }
      );
      // Show operation tracking toast
      if (result.operationId) {
        toast(
          `Operation ${result.operationId.slice(0, 8)}... started. View in Operations tab.`,
          { icon: '📋', duration: 4000 }
        );
      }
      // Refetch immediately to update UI state
      refetchAllClusters();
      setSelectedClusterToDelete(null);
      setConfirmAction(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to initiate cluster deletion: ${error.message}`);
    },
  });

  const handleConfirmAction = () => {
    switch (confirmAction) {
      case 'start':
        startFleetMutation.mutate();
        break;
      case 'stop':
        stopFleetMutation.mutate();
        break;
      case 'terminate':
        terminateFleetMutation.mutate();
        break;
      case 'deploy-ec2':
        deployEc2Mutation.mutate();
        break;
      case 'deploy-ecs':
        deployEcsMutation.mutate(undefined);
        break;
      case 'destroy-ec2':
        destroyEc2Mutation.mutate();
        break;
      case 'destroy-ecs':
        destroyEcsMutation.mutate();
        break;
      case 'cancel-ec2':
        cancelEc2Mutation.mutate();
        break;
      case 'cancel-ecs':
        cancelEcsMutation.mutate();
        break;
      case 'scale-ecs':
        scaleEcsMutation.mutate(ecsDesiredCapacity);
        break;
      case 'run-ecs-task':
        runEcsTaskMutation.mutate(selectedClusterForTaskOp || undefined);
        break;
      case 'stop-ecs-task':
        if (selectedTask && selectedClusterForTaskOp) {
          stopEcsTaskMutation.mutate({ clusterName: selectedClusterForTaskOp, task: selectedTask });
        } else if (selectedTask && deploymentStatus?.ecs?.ecsClusterName) {
          // Fallback for Fleet tab usage
          stopEcsTaskMutation.mutate({ clusterName: deploymentStatus.ecs.ecsClusterName, task: selectedTask });
        }
        break;
      case 'cleanup-stuck-tasks':
        if (selectedClusterForTaskOp && pendingTasksToCleanup.length > 0) {
          cleanupStuckTasksMutation.mutate({ clusterName: selectedClusterForTaskOp, tasks: pendingTasksToCleanup });
        } else if (deploymentStatus?.ecs?.ecsClusterName && ecsClusterDetails?.tasks) {
          // Fallback for Fleet tab usage
          const stuckTasks = ecsClusterDetails.tasks.filter(t => t.lastStatus === 'PENDING');
          cleanupStuckTasksMutation.mutate({ clusterName: deploymentStatus.ecs.ecsClusterName, tasks: stuckTasks });
        }
        break;
      case 'repair-ecs':
        repairEcsMutation.mutate();
        break;
    }
  };

  const isAnyMutationLoading =
    startFleetMutation.isPending ||
    stopFleetMutation.isPending ||
    terminateFleetMutation.isPending ||
    startInstanceMutation.isPending ||
    stopInstanceMutation.isPending ||
    hibernateInstanceMutation.isPending ||
    terminateInstanceMutation.isPending ||
    deployEc2Mutation.isPending ||
    deployEcsMutation.isPending ||
    destroyEc2Mutation.isPending ||
    destroyEcsMutation.isPending ||
    cancelEc2Mutation.isPending ||
    cancelEcsMutation.isPending ||
    scaleEcsMutation.isPending ||
    runEcsTaskMutation.isPending ||
    stopEcsTaskMutation.isPending ||
    cleanupStuckTasksMutation.isPending ||
    repairEcsMutation.isPending ||
    terminateEcsInstanceMutation.isPending ||
    terminateBulkInstancesMutation.isPending ||
    deleteEcsClusterMutation.isPending;

  const getStateColor = (state: string) => {
    switch (state) {
      case 'running':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
      case 'stopped':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'pending':
      case 'stopping':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'terminated':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-surface-600/50 text-surface-300 border-surface-500/30';
    }
  };

  const getStateDot = (state: string) => {
    switch (state) {
      case 'running':
        return 'bg-emerald-400 animate-pulse';
      case 'stopped':
        return 'bg-yellow-400';
      case 'pending':
      case 'stopping':
        return 'bg-blue-400 animate-pulse';
      default:
        return 'bg-surface-400';
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-surface-100 mb-1 sm:mb-2">Infrastructure</h1>
          <p className="text-sm sm:text-base text-surface-400">
            Deploy and manage your GPU infrastructure
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(deploymentUpdatedAt || fleetUpdatedAt) && (
            <span className="text-xs text-surface-500 hidden sm:flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {formatDistanceToNow(new Date(Math.max(deploymentUpdatedAt || 0, fleetUpdatedAt || 0)), { addSuffix: true })}
            </span>
          )}
          <button
            onClick={() => {
              refetch();
              queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
              queryClient.invalidateQueries({ queryKey: ['fleet-status'] });
              queryClient.invalidateQueries({ queryKey: ['cost-savings'] });
              queryClient.invalidateQueries({ queryKey: ['ecs-cluster-details'] });
            }}
            disabled={isLoading || deploymentsLoading}
            className="btn-ghost"
          >
            <RefreshCw className={clsx('w-4 h-4', (isLoading || deploymentsLoading) && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants} className="flex gap-2 border-b border-surface-800 overflow-x-auto">
        <button
          onClick={() => {
            setActiveTab('gpu-containers');
          }}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px',
            activeTab === 'gpu-containers'
              ? 'text-gpu-400 border-gpu-500'
              : 'text-surface-400 border-transparent hover:text-surface-200'
          )}
        >
          <Container className="w-4 h-4" />
          GPU Containers
          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gpu-500/20 text-gpu-300 border border-gpu-500/30">
            RECOMMENDED
          </span>
        </button>
        <button
          onClick={() => {
            setActiveTab('deployment');
          }}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px',
            activeTab === 'deployment'
              ? 'text-gpu-400 border-gpu-500'
              : 'text-surface-400 border-transparent hover:text-surface-200'
          )}
        >
          <Cloud className="w-4 h-4" />
          GPU Instances
        </button>
        <button
          onClick={() => {
            setActiveTab('fleet');
          }}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px',
            activeTab === 'fleet'
              ? 'text-gpu-400 border-gpu-500'
              : 'text-surface-400 border-transparent hover:text-surface-200'
          )}
        >
          <Server className="w-4 h-4" />
          Fleet Control
        </button>
      </motion.div>

      {/* Deployment Tab */}
      {activeTab === 'deployment' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* Sub-tabs */}
          <div className="flex gap-2 border-b border-surface-700">
            <button
              onClick={() => setDeploymentSubTab('instances')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                deploymentSubTab === 'instances'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Server className="w-4 h-4" />
              Instances
            </button>
            <button
              onClick={() => setDeploymentSubTab('network')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                deploymentSubTab === 'network'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Globe className="w-4 h-4" />
              Network
            </button>
            <button
              onClick={() => setDeploymentSubTab('api-keys')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                deploymentSubTab === 'api-keys'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Settings2 className="w-4 h-4" />
              API Keys
            </button>
          </div>

          {/* Instances Sub-tab */}
          {deploymentSubTab === 'instances' && (
            <div className="space-y-6">
          {/* Infrastructure Status */}
          {deploymentStatus?.ec2?.status === 'deployed' ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card bg-gradient-to-br from-emerald-600/10 to-teal-600/10 border-emerald-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Cloud className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-surface-100">Infrastructure Deployed</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
                          refetch();
                        }}
                        className="btn-ghost text-surface-400 hover:bg-surface-700 text-sm px-3 py-1"
                        title="Refresh deployment status"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setConfirmAction('destroy-ec2')}
                        disabled={isAnyMutationLoading}
                        className="btn-ghost text-red-400 hover:bg-red-500/10 text-sm px-3 py-1"
                      >
                        <CloudOff className="w-4 h-4" />
                        Destroy
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    {deploymentStatus.ec2.region && (
                      <div>
                        <span className="text-surface-400">Region:</span>
                        <p className="text-surface-200 font-mono mt-1">{deploymentStatus.ec2.region}</p>
                      </div>
                    )}
                    {deploymentStatus.ec2.vpcId && (
                      <div>
                        <span className="text-surface-400">VPC ID:</span>
                        <p className="text-surface-200 font-mono text-xs mt-1">{deploymentStatus.ec2.vpcId}</p>
                      </div>
                    )}
                    {deploymentStatus.ec2.deployedAt && (
                      <div>
                        <span className="text-surface-400">Deployed:</span>
                        <p className="text-surface-200 mt-1">
                          {formatDistanceToNow(new Date(deploymentStatus.ec2.deployedAt), { addSuffix: true })}
                        </p>
                      </div>
                    )}
                  </div>
                  {gpuJobs && gpuJobs.some(j => j.status === 'failed' && j.errorMessage?.includes('launch template')) && (
                    <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <p className="text-sm text-blue-300 mb-2">
                        ℹ️ <strong>Previous Launch Template Errors</strong>
                      </p>
                      <p className="text-xs text-surface-300">
                        Some older instances failed due to missing launch templates. This has been fixed - new instances will automatically create their own network infrastructure. Try creating a new instance!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ) : deploymentStatus?.ec2?.status === 'deploying' ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card bg-gradient-to-br from-blue-600/10 to-cyan-600/10 border-blue-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-surface-100">Deploying Infrastructure</h3>
                    <button
                      onClick={() => {
                        queryClient.invalidateQueries({ queryKey: ['deployment-status'] });
                        toast.success('Status refreshed');
                      }}
                      className="btn-ghost text-surface-400 hover:bg-surface-700 text-xs px-2 py-1"
                      title="Refresh deployment status"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Check Status
                    </button>
                  </div>
                  <p className="text-sm text-surface-300 mb-4">
                    Setting up AWS resources including VPC, security groups, and launch templates. This usually takes 2-5 minutes.
                  </p>
                  {deploymentStatus?.ec2?.updatedAt && (
                    <div className="mb-4 text-xs text-surface-400">
                      Started: {formatDistanceToNow(new Date(deploymentStatus.ec2.updatedAt), { addSuffix: true })}
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 mb-4">
                    <p className="text-xs text-blue-300">
                      You can safely navigate away - the deployment will continue in the background. Refresh this page or click "Check Status" to see progress.
                    </p>
                  </div>
                  {deploymentStatus?.ec2?.updatedAt && 
                   Date.now() - new Date(deploymentStatus.ec2.updatedAt).getTime() > 10 * 60 * 1000 && (
                    <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                      <p className="text-sm text-orange-300 font-medium mb-1">⚠️ Deployment Taking Longer Than Expected</p>
                      <p className="text-xs text-orange-400 mb-2">
                        The deployment has been running for over 10 minutes. This might indicate an issue.
                      </p>
                      <button
                        onClick={() => {
                          if (confirm('Cancel the current deployment and start fresh? This will not affect existing AWS resources.')) {
                            setConfirmAction('cancel-ec2');
                          }
                        }}
                        className="btn-ghost text-orange-400 hover:bg-orange-500/10 text-xs px-3 py-1.5"
                      >
                        <XCircle className="w-3 h-3" />
                        Cancel & Retry
                      </button>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmAction('cancel-ec2')}
                      disabled={cancelEc2Mutation.isPending}
                      className="btn-ghost text-red-400 hover:bg-red-500/10 text-sm"
                    >
                      {cancelEc2Mutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Cancel Deployment
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : deploymentStatus?.ec2?.status === 'destroying' ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card bg-gradient-to-br from-orange-600/10 to-red-600/10 border-orange-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-surface-100 mb-2">Destroying Infrastructure</h3>
                  <p className="text-sm text-surface-300 mb-4">
                    Tearing down AWS resources. All running instances will be terminated.
                  </p>
                </div>
              </div>
            </motion.div>
          ) : null}

          {/* GPU Instances List */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-gpu-400" />
                <h2 className="text-xl font-semibold text-surface-100">GPU Instances</h2>
                {deploymentStatus?.ec2?.status === 'deployed' && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Infrastructure Ready
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCreateGpuJob(!showCreateGpuJob)}
                className="btn-primary flex items-center gap-2"
                title="Create Instance"
              >
                <Plus className="w-4 h-4" />
                {showCreateGpuJob ? 'Cancel' : 'Create Instance'}
              </button>
            </div>

            {/* Create Instance Form */}
            {showCreateGpuJob && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 card bg-gradient-to-br from-gpu-600/10 to-teal-600/10 border-gpu-500/20"
              >
                <h3 className="text-lg font-semibold text-surface-100 mb-2 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-gpu-400" />
                  Launch GPU Instance
                </h3>
                <p className="text-sm text-surface-400 mb-6">Configure your GPU instance with detailed settings for compute, storage, network, and pricing options.</p>
                
                <div className="space-y-6">
                  {/* Instance Type */}
                  <div>
                    <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-gpu-400" />
                      Instance Type
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {INSTANCE_TYPES.map((instance) => (
                        <button
                          key={instance.type}
                          type="button"
                          onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, instanceType: instance.type })}
                          className={clsx(
                            'p-4 rounded-lg border-2 text-left transition-all relative',
                            newGpuJobConfig.instanceType === instance.type
                              ? instance.freeTier 
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-gpu-500 bg-gpu-500/10'
                              : 'border-surface-700 hover:border-surface-600 bg-surface-800/50',
                            instance.freeTier && 'ring-1 ring-emerald-500/30',
                          )}
                        >
                          {instance.freeTier && (
                            <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-bold bg-emerald-500 text-white rounded-full shadow-lg animate-pulse">
                              ★ FREE TIER
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-surface-100">
                              {instance.type}
                            </span>
                            <span className={clsx(
                              'text-xs px-1.5 py-0.5 rounded',
                              instance.freeTier 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-surface-700 text-surface-400'
                            )}>
                              {instance.cost}
                            </span>
                          </div>
                          <div className="text-sm text-surface-400 mt-1">
                            {instance.gpus > 0 ? `${instance.gpus} GPU · ` : ''}{instance.memory}GB · {instance.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Runtime & Environment */}
                  <div>
                    <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gpu-400" />
                      Configuration
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Max Runtime (hours) *</label>
                        <input
                          type="number"
                          min={1}
                          max={24}
                          value={newGpuJobConfig.maxRuntimeHours}
                          onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, maxRuntimeHours: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                        <p className="text-xs text-surface-500 mt-1">
                          Instance will be terminated after this duration
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Environment *</label>
                        <select
                          value={newGpuJobConfig.environment}
                          onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, environment: e.target.value as 'development' | 'staging' | 'production' })}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        >
                          <option value="development">Development</option>
                          <option value="staging">Staging</option>
                          <option value="production">Production</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Project (optional)</label>
                        <input
                          type="text"
                          value={newGpuJobConfig.project}
                          onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, project: e.target.value })}
                          placeholder="e.g., ml-training"
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Pricing Model *</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, useSpot: true })}
                            className={clsx(
                              'flex-1 px-4 py-2 rounded-lg border-2 transition-all',
                              newGpuJobConfig.useSpot
                                ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                : 'border-surface-700 text-surface-400',
                            )}
                          >
                            Spot (~70% off)
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, useSpot: false })}
                            disabled={newGpuJobConfig.environment !== 'production'}
                            className={clsx(
                              'flex-1 px-4 py-2 rounded-lg border-2 transition-all',
                              !newGpuJobConfig.useSpot
                                ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                : 'border-surface-700 text-surface-400',
                              newGpuJobConfig.environment !== 'production' && 'opacity-50 cursor-not-allowed',
                            )}
                          >
                            On-Demand
                          </button>
                        </div>
                        {newGpuJobConfig.environment !== 'production' && (
                          <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            On-demand only available for production
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Advanced Options Toggle */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border border-surface-700 hover:border-gpu-500/50 hover:bg-surface-800/50 transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <Settings2 className="w-5 h-5 text-gpu-400" />
                        <span className="font-semibold text-surface-100">Advanced Options</span>
                        <span className="text-xs text-surface-500">(Container, Storage, Network, Security & more)</span>
                      </div>
                      {showAdvanced ? (
                        <ChevronUp className="w-5 h-5 text-surface-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-surface-400" />
                      )}
                    </button>
                  </div>

                  {/* Advanced Options Sections */}
                  {showAdvanced && (
                    <div className="space-y-6 p-4 rounded-lg border border-surface-700 bg-surface-900/30">
                      {/* Container & Image */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <Container className="w-4 h-4 text-gpu-400" />
                          Container Image
                        </h4>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {DOCKER_IMAGES.map((image) => (
                              <button
                                key={image.value}
                                type="button"
                                onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, dockerImagePreset: image.value })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-left transition-all',
                                  newGpuJobConfig.dockerImagePreset === image.value
                                    ? 'border-gpu-500 bg-gpu-500/10'
                                    : 'border-surface-700 hover:border-surface-600 bg-surface-800/50',
                                )}
                              >
                                <div className="text-sm font-medium text-surface-100">{image.label}</div>
                                <div className="text-xs text-surface-400 mt-0.5 truncate">{image.description}</div>
                              </button>
                            ))}
                          </div>
                          {newGpuJobConfig.dockerImagePreset === 'custom' && (
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Custom Docker Image</label>
                              <input
                                type="text"
                                value={newGpuJobConfig.customDockerImage}
                                onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, customDockerImage: e.target.value })}
                                placeholder="e.g., myregistry/myimage:latest"
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100 font-mono text-sm"
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Startup Command (optional)</label>
                            <input
                              type="text"
                              value={newGpuJobConfig.startupCommand}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, startupCommand: e.target.value })}
                              placeholder="e.g., python train.py --epochs 100"
                              className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100 font-mono text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Storage */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <HardDrive className="w-4 h-4 text-gpu-400" />
                          Storage
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Root Volume Size</label>
                            <div className="grid grid-cols-5 gap-1">
                              {VOLUME_SIZES.map((size) => (
                                <button
                                  key={size.value}
                                  type="button"
                                  onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, rootVolumeSize: size.value })}
                                  className={clsx(
                                    'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                    newGpuJobConfig.rootVolumeSize === size.value
                                      ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                      : 'border-surface-700 hover:border-surface-600 text-surface-300',
                                  )}
                                >
                                  <div className="text-sm font-medium">{size.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Volume Type</label>
                            <select
                              value={newGpuJobConfig.rootVolumeType}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, rootVolumeType: e.target.value as 'gp3' | 'gp2' | 'io1' | 'io2' })}
                              className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                            >
                              <option value="gp3">gp3 (Recommended)</option>
                              <option value="gp2">gp2</option>
                              <option value="io1">io1 (High IOPS)</option>
                              <option value="io2">io2 (High IOPS)</option>
                            </select>
                          </div>
                          <div className="md:col-span-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="useInstanceStore"
                                checked={newGpuJobConfig.useInstanceStore}
                                onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, useInstanceStore: e.target.checked })}
                                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                                disabled={newGpuJobConfig.instanceType.startsWith('p3') || newGpuJobConfig.instanceType.startsWith('t2') || newGpuJobConfig.instanceType.startsWith('t3')}
                              />
                              <label htmlFor="useInstanceStore" className="text-sm text-surface-300">
                                Use NVMe instance store (ephemeral high-speed storage)
                              </label>
                            </div>
                            {(newGpuJobConfig.instanceType.startsWith('p3') || newGpuJobConfig.instanceType.startsWith('t2') || newGpuJobConfig.instanceType.startsWith('t3')) && (
                              <div className="flex items-start gap-2 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-yellow-300">
                                  {newGpuJobConfig.instanceType.startsWith('p3') 
                                    ? 'p3 instances are EBS-only and do not have NVMe instance store. Use g4dn or g5 instances for NVMe storage.' 
                                    : 'Free-tier instances do not have NVMe instance store.'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Network */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <Network className="w-4 h-4 text-gpu-400" />
                          Network
                        </h4>
                        
                        {/* Network Mode Selection */}
                        <div className="mb-4">
                          <label className="block text-sm text-surface-300 mb-2">Network Mode</label>
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setNewGpuJobConfig({ 
                                  ...newGpuJobConfig, 
                                  networkMode: 'shared', 
                                  existingVpcId: '', 
                                  existingSubnetId: '', 
                                  existingSecurityGroupId: '' 
                                });
                              }}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-left transition-all',
                                newGpuJobConfig.networkMode === 'shared'
                                  ? 'border-gpu-500 bg-gpu-500/10'
                                  : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                              )}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Layers className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-medium text-surface-100">Shared</span>
                              </div>
                              <p className="text-xs text-surface-400">Platform's default VPC</p>
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => {
                                setNewGpuJobConfig({ 
                                  ...newGpuJobConfig, 
                                  networkMode: 'dedicated', 
                                  existingVpcId: '', 
                                  existingSubnetId: '', 
                                  existingSecurityGroupId: '' 
                                });
                              }}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-left transition-all',
                                newGpuJobConfig.networkMode === 'dedicated'
                                  ? 'border-gpu-500 bg-gpu-500/10'
                                  : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                              )}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Shield className="w-4 h-4 text-emerald-400" />
                                <span className="text-sm font-medium text-surface-100">Dedicated</span>
                              </div>
                              <p className="text-xs text-surface-400">Isolated VPC per instance</p>
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, networkMode: 'existing' })}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-left transition-all',
                                newGpuJobConfig.networkMode === 'existing'
                                  ? 'border-gpu-500 bg-gpu-500/10'
                                  : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                              )}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Key className="w-4 h-4 text-orange-400" />
                                <span className="text-sm font-medium text-surface-100">Existing</span>
                              </div>
                              <p className="text-xs text-surface-400">Select existing network</p>
                            </button>
                          </div>
                        </div>

                    {/* Existing Network Selection */}
                    {newGpuJobConfig.networkMode === 'existing' && (
                      <div className="mb-4 p-4 rounded-lg border border-surface-700 bg-surface-800/50 space-y-4">
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Select VPC *</label>
                          <select
                            value={newGpuJobConfig.existingVpcId}
                            onChange={(e) => setNewGpuJobConfig({ 
                              ...newGpuJobConfig, 
                              existingVpcId: e.target.value,
                              existingSubnetId: '',
                              existingSecurityGroupId: ''
                            })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          >
                            <option value="">Select a VPC...</option>
                            {networkData?.vpcs.map((vpc) => (
                              <option key={vpc.vpcId} value={vpc.vpcId}>
                                {vpc.tags?.['Name'] || vpc.vpcId} ({vpc.cidrBlock}) {vpc.isDefault ? '- Default' : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {newGpuJobConfig.existingVpcId && (
                          <>
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Select Subnet *</label>
                              <select
                                value={newGpuJobConfig.existingSubnetId}
                                onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, existingSubnetId: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value="">Select a subnet...</option>
                                {networkData?.subnets
                                  .filter(subnet => subnet.vpcId === newGpuJobConfig.existingVpcId)
                                  .map((subnet) => (
                                    <option key={subnet.subnetId} value={subnet.subnetId}>
                                      {subnet.tags?.['Name'] || subnet.subnetId} ({subnet.cidrBlock} - {subnet.availabilityZone})
                                    </option>
                                  ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Select Security Group *</label>
                              <select
                                value={newGpuJobConfig.existingSecurityGroupId}
                                onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, existingSecurityGroupId: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value="">Select a security group...</option>
                                {networkData?.securityGroups
                                  .filter(sg => sg.vpcId === newGpuJobConfig.existingVpcId)
                                  .map((sg) => (
                                    <option key={sg.groupId} value={sg.groupId}>
                                      {sg.groupName} ({sg.groupId})
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </>
                        )}

                        {!networkData?.vpcs.length && (
                          <div className="text-center py-4 text-surface-400">
                            <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No existing networks found</p>
                            <p className="text-xs text-surface-500">Deploy infrastructure first or select a different network mode</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Network mode descriptions */}
                        {/* Network mode descriptions */}
                        {newGpuJobConfig.networkMode === 'shared' && (
                          <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <p className="text-xs text-blue-300">
                              <strong>Shared Network:</strong> Uses the platform's default VPC. Multiple instances share the same network for cost efficiency.
                            </p>
                          </div>
                        )}

                        {newGpuJobConfig.networkMode === 'dedicated' && (
                          <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                            <p className="text-xs text-emerald-300">
                              <strong>Dedicated Network:</strong> Creates a separate VPC for this instance. Provides complete network isolation. Auto-cleaned on termination.
                            </p>
                          </div>
                        )}

                        {newGpuJobConfig.networkMode === 'existing' && (
                          <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                            <p className="text-xs text-orange-300">
                              <strong>Existing Network:</strong> Use your own VPC. Useful for integrating with existing AWS infrastructure.
                            </p>
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Availability Zone</label>
                            <select
                              value={newGpuJobConfig.availabilityZone}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, availabilityZone: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                            >
                              {AVAILABILITY_ZONES.map((az) => (
                                <option key={az.value} value={az.value}>{az.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="assignPublicIp"
                              checked={newGpuJobConfig.assignPublicIp}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, assignPublicIp: e.target.checked })}
                              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                            />
                            <label htmlFor="assignPublicIp" className="text-sm text-surface-300">
                              Assign public IP address
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Cost & Lifecycle Controls */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gpu-400" />
                          Cost & Lifecycle
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Max Job Cost</label>
                            <div className="grid grid-cols-4 gap-1">
                              {MAX_COST_PRESETS.map((preset) => (
                                <button
                                  key={preset.value}
                                  type="button"
                                  onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, maxCostUsd: preset.value })}
                                  className={clsx(
                                    'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                    newGpuJobConfig.maxCostUsd === preset.value
                                      ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                      : 'border-surface-700 hover:border-surface-600 text-surface-300',
                                  )}
                                >
                                  <div className="text-sm font-medium">{preset.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-surface-300 mb-2">Idle Timeout</label>
                            <div className="grid grid-cols-3 gap-1">
                              {IDLE_TIMEOUTS.map((timeout) => (
                                <button
                                  key={timeout.value}
                                  type="button"
                                  onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, idleTimeoutMinutes: timeout.value })}
                                  className={clsx(
                                    'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                    newGpuJobConfig.idleTimeoutMinutes === timeout.value
                                      ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                      : 'border-surface-700 hover:border-surface-600 text-surface-300',
                                  )}
                                >
                                  <div className="text-xs font-medium">{timeout.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          {newGpuJobConfig.useSpot && (
                            <>
                              <div>
                                <label className="block text-sm text-surface-300 mb-2">Spot Interruption Behavior</label>
                                <select
                                  value={newGpuJobConfig.spotInterruptionBehavior}
                                  onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, spotInterruptionBehavior: e.target.value as 'terminate' | 'stop' | 'hibernate' })}
                                  className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                                >
                                  <option value="terminate">Terminate instance</option>
                                  <option value="stop">Stop instance</option>
                                  <option value="hibernate">Hibernate instance</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="spotFallback"
                                  checked={newGpuJobConfig.spotFallbackToOnDemand}
                                  onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, spotFallbackToOnDemand: e.target.checked })}
                                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                                />
                                <label htmlFor="spotFallback" className="text-sm text-surface-300">
                                  Fall back to on-demand if spot unavailable
                                </label>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Security */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <Key className="w-4 h-4 text-gpu-400" />
                          Security
                        </h4>
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">SSH Key Management</label>
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              type="button"
                              onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, sshKeyMode: 'generate' })}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-center transition-all',
                                newGpuJobConfig.sshKeyMode === 'generate'
                                  ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                  : 'border-surface-700 hover:border-surface-600 text-surface-300',
                              )}
                            >
                              <div className="text-sm font-medium">Generate</div>
                              <div className="text-xs text-surface-500 mt-1">Auto-create key pair</div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, sshKeyMode: 'existing' })}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-center transition-all',
                                newGpuJobConfig.sshKeyMode === 'existing'
                                  ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                  : 'border-surface-700 hover:border-surface-600 text-surface-300',
                              )}
                            >
                              <div className="text-sm font-medium">Existing</div>
                              <div className="text-xs text-surface-500 mt-1">Use existing key</div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewGpuJobConfig({ ...newGpuJobConfig, sshKeyMode: 'none' })}
                              className={clsx(
                                'p-3 rounded-lg border-2 text-center transition-all',
                                newGpuJobConfig.sshKeyMode === 'none'
                                  ? 'border-gpu-500 bg-gpu-500/10 text-gpu-400'
                                  : 'border-surface-700 hover:border-surface-600 text-surface-300',
                              )}
                            >
                              <div className="text-sm font-medium">None</div>
                              <div className="text-xs text-surface-500 mt-1">No SSH access</div>
                            </button>
                          </div>
                          {newGpuJobConfig.sshKeyMode === 'existing' && (
                            <div className="mt-3">
                              <input
                                type="text"
                                value={newGpuJobConfig.existingSshKeyName}
                                onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, existingSshKeyName: e.target.value })}
                                placeholder="Enter existing SSH key name"
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Notifications */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <Bell className="w-4 h-4 text-gpu-400" />
                          Notifications
                        </h4>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newGpuJobConfig.notifyOnComplete}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, notifyOnComplete: e.target.checked })}
                              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                            />
                            <span className="text-sm text-surface-300">Notify when job completes</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newGpuJobConfig.notifyOnFailure}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, notifyOnFailure: e.target.checked })}
                              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                            />
                            <span className="text-sm text-surface-300">Notify on failure</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newGpuJobConfig.requireHeartbeat}
                              onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, requireHeartbeat: e.target.checked })}
                              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-gpu-500 focus:ring-gpu-500"
                            />
                            <span className="text-sm text-surface-300">Require heartbeat monitoring</span>
                          </label>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div>
                        <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                          <Tag className="w-4 h-4 text-gpu-400" />
                          Metadata
                        </h4>
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Instance Name (optional)</label>
                          <input
                            type="text"
                            value={newGpuJobConfig.name}
                            onChange={(e) => setNewGpuJobConfig({ ...newGpuJobConfig, name: e.target.value })}
                            placeholder="e.g., training-run-001"
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cost Estimate */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-gpu-600/10 to-blue-600/10 border border-gpu-500/30">
                    <h4 className="text-sm font-semibold text-surface-100 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gpu-400" />
                      Cost Estimate
                    </h4>

                    {estimating ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gpu-400" />
                      </div>
                    ) : costEstimate ? (
                      <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="text-2xl font-bold text-surface-100">
                            ${costEstimate.estimatedCostUsd.toFixed(2)}
                          </div>
                          <div className="text-xs text-surface-400">
                            Estimated total cost
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-surface-400">Hourly rate:</span>
                            <span className="text-surface-200 font-medium">
                              ${costEstimate.hourlyRateUsd.toFixed(2)}/hr
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-surface-400">Duration:</span>
                            <span className="text-surface-200 font-medium">{newGpuJobConfig.maxRuntimeHours}h</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-surface-400">GPU count:</span>
                            <span className="text-surface-200 font-medium">{costEstimate.gpuCount}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-surface-400">Pricing:</span>
                            <span className={clsx(
                              'font-medium',
                              newGpuJobConfig.useSpot ? 'text-green-400' : 'text-surface-200'
                            )}>
                              {newGpuJobConfig.useSpot ? 'Spot (~70% off)' : 'On-Demand'}
                            </span>
                          </div>
                        </div>

                        {newGpuJobConfig.useSpot && (
                          <div className="flex items-start gap-2 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-yellow-300">
                              Spot instances can be interrupted with 2-minute warning. Use On-Demand for critical workloads.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-surface-500 text-sm">
                        Configure instance to see cost estimate
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3 pt-6 border-t border-surface-700">
                    <button
                      type="button"
                      onClick={() => {
                        // Validate existing network selection
                        if (newGpuJobConfig.networkMode === 'existing') {
                          if (!newGpuJobConfig.existingVpcId || !newGpuJobConfig.existingSubnetId || !newGpuJobConfig.existingSecurityGroupId) {
                            toast.error('Please select a VPC, subnet, and security group for existing network mode');
                            return;
                          }
                        }
                        
                        // Get the final Docker image value
                        const dockerImage = newGpuJobConfig.dockerImagePreset === 'custom' 
                          ? newGpuJobConfig.customDockerImage 
                          : newGpuJobConfig.dockerImagePreset;
                        
                        // Clean up config before sending
                        const config: any = {
                          instanceType: newGpuJobConfig.instanceType,
                          useSpot: newGpuJobConfig.useSpot,
                          maxRuntimeHours: newGpuJobConfig.maxRuntimeHours,
                          environment: newGpuJobConfig.environment,
                        };

                        // Add optional basic fields
                        if (newGpuJobConfig.project) config.project = newGpuJobConfig.project;

                        // Add advanced options if enabled
                        if (showAdvanced) {
                          // Storage
                          if (newGpuJobConfig.rootVolumeSize !== 100) config.rootVolumeSize = newGpuJobConfig.rootVolumeSize;
                          if (newGpuJobConfig.rootVolumeType !== 'gp3') config.rootVolumeType = newGpuJobConfig.rootVolumeType;
                          if (newGpuJobConfig.useInstanceStore) config.useInstanceStore = newGpuJobConfig.useInstanceStore;

                          // Networking
                          if (!newGpuJobConfig.assignPublicIp) config.assignPublicIp = newGpuJobConfig.assignPublicIp;
                          config.networkMode = newGpuJobConfig.networkMode;
                          config.useDedicatedNetwork = newGpuJobConfig.networkMode === 'dedicated';
                          if (newGpuJobConfig.networkMode === 'existing') {
                            config.existingVpcId = newGpuJobConfig.existingVpcId;
                            config.existingSubnetId = newGpuJobConfig.existingSubnetId;
                            config.existingSecurityGroupId = newGpuJobConfig.existingSecurityGroupId;
                          }
                          if (newGpuJobConfig.availabilityZone) config.availabilityZone = newGpuJobConfig.availabilityZone;

                          // Spot
                          if (newGpuJobConfig.spotInterruptionBehavior !== 'terminate') config.spotInterruptionBehavior = newGpuJobConfig.spotInterruptionBehavior;
                          if (newGpuJobConfig.spotFallbackToOnDemand) config.spotFallbackToOnDemand = newGpuJobConfig.spotFallbackToOnDemand;

                          // Container
                          if (dockerImage) config.dockerImage = dockerImage;
                          if (newGpuJobConfig.startupCommand) config.startupCommand = newGpuJobConfig.startupCommand;

                          // Lifecycle
                          if (newGpuJobConfig.idleTimeoutMinutes > 0) config.idleTimeoutMinutes = newGpuJobConfig.idleTimeoutMinutes;
                          if (newGpuJobConfig.requireHeartbeat) config.requireHeartbeat = newGpuJobConfig.requireHeartbeat;

                          // Notifications
                          if (newGpuJobConfig.notifyOnComplete) config.notifyOnComplete = newGpuJobConfig.notifyOnComplete;
                          if (newGpuJobConfig.notifyOnFailure) config.notifyOnFailure = newGpuJobConfig.notifyOnFailure;

                          // Resource Limits
                          if (newGpuJobConfig.maxCostUsd > 0) config.maxCostUsd = newGpuJobConfig.maxCostUsd;

                          // Metadata
                          if (Object.keys(newGpuJobConfig.selectedTags).length > 0) config.customTags = newGpuJobConfig.selectedTags;
                          if (newGpuJobConfig.name) config.name = newGpuJobConfig.name;

                          // Security
                          if (newGpuJobConfig.sshKeyMode !== 'generate') config.sshKeyMode = newGpuJobConfig.sshKeyMode;
                          if (newGpuJobConfig.sshKeyMode === 'existing' && newGpuJobConfig.existingSshKeyName) {
                            config.existingSshKeyName = newGpuJobConfig.existingSshKeyName;
                          }
                        }

                        createGpuJobMutation.mutate(config);
                      }}
                      disabled={createGpuJobMutation.isPending || (newGpuJobConfig.networkMode === 'existing' && (!newGpuJobConfig.existingVpcId || !newGpuJobConfig.existingSubnetId || !newGpuJobConfig.existingSecurityGroupId))}
                      className="flex-1 btn-primary flex items-center justify-center gap-2"
                    >
                      {createGpuJobMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Launch Instance
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateGpuJob(false);
                        setShowAdvanced(false);
                      }}
                      className="flex-1 btn-ghost"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Instances List */}
            {gpuJobs && gpuJobs.length > 0 ? (
              <>
                {/* Status Filter */}
                <div className="flex items-center gap-3 mb-4">
                  <Filter className="w-4 h-4 text-surface-400" />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setSelectedStatusFilter('all');
                        setInstancesPage(1);
                      }}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                        selectedStatusFilter === 'all'
                          ? 'bg-gpu-500 text-white'
                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      )}
                    >
                      All ({gpuJobs.length})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatusFilter('running');
                        setInstancesPage(1);
                      }}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                        selectedStatusFilter === 'running'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      )}
                    >
                      Running ({gpuJobs.filter(j => j.status === 'running').length})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatusFilter('pending');
                        setInstancesPage(1);
                      }}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                        selectedStatusFilter === 'pending'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      )}
                    >
                      Pending ({gpuJobs.filter(j => j.status === 'pending' || j.status === 'provisioning').length})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatusFilter('failed');
                        setInstancesPage(1);
                      }}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                        selectedStatusFilter === 'failed'
                          ? 'bg-red-500 text-white'
                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      )}
                    >
                      Failed ({gpuJobs.filter(j => j.status === 'failed').length})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStatusFilter('completed');
                        setInstancesPage(1);
                      }}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                        selectedStatusFilter === 'completed'
                          ? 'bg-blue-500 text-white'
                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      )}
                    >
                      Completed ({gpuJobs.filter(j => j.status === 'completed' || j.status === 'terminated' || j.status === 'cancelled').length})
                    </button>
                  </div>
                </div>

                {/* Date Range and Page Size Filters */}
                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  {/* Date Range Filter */}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surface-400" />
                    <span className="text-sm text-surface-400">Date Range:</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setInstancesPage(1);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                      placeholder="From"
                    />
                    <span className="text-surface-500">to</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setInstancesPage(1);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                      placeholder="To"
                    />
                    {(dateFrom || dateTo) && (
                      <button
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                          setInstancesPage(1);
                        }}
                        className="px-2 py-1 rounded-lg text-xs bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Page Size Selector */}
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-surface-400">Per page:</span>
                    <select
                      value={instancesPerPage}
                      onChange={(e) => {
                        setInstancesPerPage(Number(e.target.value));
                        setInstancesPage(1);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors cursor-pointer"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                {(() => {
                  // Filter instances by status
                  let filteredJobs = selectedStatusFilter === 'all' 
                    ? gpuJobs 
                    : selectedStatusFilter === 'pending'
                    ? gpuJobs.filter(j => j.status === 'pending' || j.status === 'provisioning')
                    : selectedStatusFilter === 'completed'
                    ? gpuJobs.filter(j => j.status === 'completed' || j.status === 'terminated' || j.status === 'cancelled')
                    : gpuJobs.filter(j => j.status === selectedStatusFilter);
                  
                  // Filter by date range
                  if (dateFrom || dateTo) {
                    filteredJobs = filteredJobs.filter(j => {
                      if (!j.createdAt) return false;
                      const jobDate = new Date(j.createdAt);
                      const fromDate = dateFrom ? new Date(dateFrom) : null;
                      const toDate = dateTo ? new Date(dateTo) : null;
                      
                      // Set time to start/end of day for proper comparison
                      if (fromDate) fromDate.setHours(0, 0, 0, 0);
                      if (toDate) toDate.setHours(23, 59, 59, 999);
                      
                      if (fromDate && jobDate < fromDate) return false;
                      if (toDate && jobDate > toDate) return false;
                      return true;
                    });
                  }
                  
                  // Calculate pagination
                  const totalPages = Math.ceil(filteredJobs.length / instancesPerPage);
                  const startIndex = (instancesPage - 1) * instancesPerPage;
                  const endIndex = startIndex + instancesPerPage;
                  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);
                  
                  return (
                    <>
                      {paginatedJobs.map((job) => (
                  <div
                    key={job.id}
                    className="relative p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-gpu-500/50 hover:bg-surface-800/80 transition-all cursor-pointer"
                    onClick={() => setSelectedInstanceForDetails(job.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={clsx(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
                            job.status === 'running' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                            job.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                            job.status === 'stopping' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                            job.status === 'stopped' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                            job.status === 'completed' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                            job.status === 'failed' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                            job.status === 'cancelled' ? 'bg-gray-500/20 text-gray-300 border-gray-500/30' :
                            'bg-surface-600/50 text-surface-300 border-surface-500/30'
                          )}>
                            <span className={clsx(
                              'w-1.5 h-1.5 rounded-full',
                              job.status === 'running' && 'bg-emerald-400 animate-pulse',
                              job.status === 'pending' && 'bg-yellow-400 animate-pulse'
                            )} />
                            {job.status}
                          </span>
                          <span className="font-mono text-sm text-gpu-400">
                            {job.id}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-300">
                            {job.instanceType}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-surface-400">
                          {job.instanceId && (
                            <div className="flex items-center gap-2">
                              <Server className="w-4 h-4" />
                              <span className="font-mono text-xs">{job.instanceId}</span>
                            </div>
                          )}
                          {job.createdAt && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>
                                {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span>~${job.estimatedCostUsd?.toFixed(2) || '0.00'}/hr</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4" />
                            <span>{job.useSpot ? 'Spot' : 'On-Demand'}</span>
                          </div>
                        </div>
                        
                        {/* Error Message */}
                        {job.status === 'failed' && job.errorMessage && (
                          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                            <p className="text-sm text-red-300 font-medium mb-1">Error:</p>
                            <p className="text-xs text-red-400">{job.errorMessage}</p>
                          </div>
                        )}
                      </div>

                      {/* Instance Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInstanceForDetails(job.id);
                          }}
                          className="p-2 rounded-lg bg-gpu-500/20 text-gpu-400 hover:bg-gpu-500/30 transition-colors"
                          title="View details"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        {/* Instance action buttons */}
                        {job.instanceId && (job.status === 'running' || job.status === 'pending' || job.status === 'provisioning') && (
                          <>
                            {/* Stop button (yellow/pause) - for on-demand instances only (spot instances cannot be stopped) */}
                            {!job.useSpot && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  stopInstanceMutation.mutate(job.instanceId!);
                                }}
                                disabled={isAnyMutationLoading || pendingJobOperations.has(job.id)}
                                className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Stop instance"
                              >
                                <Pause className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Hibernate button (indigo/moon) - for on-demand instances only (spot instances don't support hibernation) */}
                            {!job.useSpot && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  hibernateInstanceMutation.mutate(job.instanceId!);
                                }}
                                disabled={isAnyMutationLoading || pendingJobOperations.has(job.id)}
                                className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Hibernate instance (preserves memory state)"
                              >
                                <Moon className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Terminate button (red/power) - for all running instances */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                terminateInstanceMutation.mutate(job.instanceId!);
                              }}
                              disabled={isAnyMutationLoading || pendingJobOperations.has(job.id)}
                              className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Terminate instance"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {/* Start button (green/play) - shown for stopped instances that can be restarted */}
                        {/* Note: Spot instances can't be stopped/started, only terminated */}
                        {job.instanceId && !job.useSpot && job.status === 'stopped' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startInstanceMutation.mutate(job.instanceId!);
                              }}
                              disabled={isAnyMutationLoading || pendingJobOperations.has(job.id)}
                              className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Start instance"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            
                            {/* Terminate button for stopped instances */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                terminateInstanceMutation.mutate(job.instanceId!);
                              }}
                              disabled={isAnyMutationLoading || pendingJobOperations.has(job.id)}
                              className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Terminate instance"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {/* Cancel button - for jobs without EC2 instance yet */}
                        {!job.instanceId && (job.status === 'running' || job.status === 'pending' || job.status === 'provisioning') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelGpuJobMutation.mutate(job.id);
                            }}
                            disabled={pendingJobOperations.has(job.id)}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Cancel job"
                          >
                            {pendingJobOperations.has(job.id) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Power className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        
                        {/* Remove button - for completed/failed/cancelled/terminated jobs */}
                        {(job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled' || job.status === 'terminated') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setJobToRemove(job.id);
                              setConfirmAction('remove-gpu-job');
                            }}
                            disabled={pendingJobOperations.has(job.id)}
                            className="p-2 rounded-lg bg-surface-700/50 text-surface-400 hover:bg-red-500/20 hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove job record"
                          >
                            {pendingJobOperations.has(job.id) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                      ))}
                      
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between pt-4 border-t border-surface-700">
                          <div className="text-sm text-surface-400">
                            Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length} instances
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setInstancesPage(Math.max(1, instancesPage - 1))}
                              disabled={instancesPage === 1}
                              className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                // Show first, last, current, and pages around current
                                if (
                                  page === 1 ||
                                  page === totalPages ||
                                  (page >= instancesPage - 1 && page <= instancesPage + 1)
                                ) {
                                  return (
                                    <button
                                      key={page}
                                      onClick={() => setInstancesPage(page)}
                                      className={clsx(
                                        'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                                        page === instancesPage
                                          ? 'bg-gpu-500 text-white'
                                          : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                                      )}
                                    >
                                      {page}
                                    </button>
                                  );
                                } else if (
                                  page === instancesPage - 2 ||
                                  page === instancesPage + 2
                                ) {
                                  return <span key={page} className="text-surface-500">...</span>;
                                }
                                return null;
                              })}
                            </div>
                            <button
                              onClick={() => setInstancesPage(Math.min(totalPages, instancesPage + 1))}
                              disabled={instancesPage === totalPages}
                              className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Server className="w-12 h-12 text-surface-600 mx-auto mb-4" />
                <p className="text-surface-400">No GPU instances yet</p>
                <p className="text-surface-500 text-sm mt-2">
                  Create your first GPU instance to get started
                </p>
              </div>
            )}
          </div>
            </div>
          )}

          {/* Network Sub-tab - Using NetworkPanel Component */}
          {deploymentSubTab === 'network' && (
            <NetworkPanel 
              mode="instances" 
              onRefresh={() => {
                queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
                queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
              }}
            />
          )}

          {/* API Keys Sub-tab */}
          {deploymentSubTab === 'api-keys' && (
            <div className="space-y-6">
              {/* Instance Selection */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings2 className="w-5 h-5 text-gpu-400" />
                    <h3 className="text-lg font-semibold text-surface-100">Instance API Keys</h3>
                  </div>
                  {selectedGpuInstanceForApiKey && (
                    <button
                      onClick={() => setShowCreateGpuApiKey(true)}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create API Key
                    </button>
                  )}
                </div>

                {/* Instance Selector */}
                {gpuJobs && gpuJobs.length > 0 ? (
                  <div className="mb-6">
                    <label className="block text-sm text-surface-300 mb-2">Select Instance</label>
                    <select
                      value={selectedGpuInstanceForApiKey || ''}
                      onChange={(e) => setSelectedGpuInstanceForApiKey(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                    >
                      <option value="">-- Select an instance --</option>
                      {gpuJobs.map((job) => (
                        <option key={job.id} value={job.instanceId || job.id}>
                          {job.instanceId || job.id} ({job.instanceType} - {job.status})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="text-center py-8 text-surface-400">
                    <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No instances available</p>
                    <p className="text-sm text-surface-500 mt-1">Create a GPU instance first to manage API keys</p>
                  </div>
                )}

                {/* Create API Key Form */}
                {showCreateGpuApiKey && selectedGpuInstanceForApiKey && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-6 p-4 rounded-lg bg-gradient-to-br from-gpu-600/10 to-teal-600/10 border border-gpu-500/20"
                  >
                    <h4 className="text-sm font-semibold text-surface-100 mb-4">Create New API Key</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Key Name</label>
                        <input
                          type="text"
                          value={newGpuApiKeyConfig.name}
                          onChange={(e) => setNewGpuApiKeyConfig({ ...newGpuApiKeyConfig, name: e.target.value })}
                          placeholder={`${selectedGpuInstanceForApiKey}-api-key`}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Scopes</label>
                        <div className="space-y-2">
                          {['jobs:read', 'jobs:write', 'jobs:delete', 'usage:read'].map((scope) => (
                            <label key={scope} className="flex items-center gap-2 text-sm text-surface-300">
                              <input
                                type="checkbox"
                                checked={newGpuApiKeyConfig.scopes.includes(scope as ApiKeyScope)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewGpuApiKeyConfig({
                                      ...newGpuApiKeyConfig,
                                      scopes: [...newGpuApiKeyConfig.scopes, scope as ApiKeyScope]
                                    });
                                  } else {
                                    setNewGpuApiKeyConfig({
                                      ...newGpuApiKeyConfig,
                                      scopes: newGpuApiKeyConfig.scopes.filter(s => s !== scope)
                                    });
                                  }
                                }}
                                className="w-4 h-4 rounded border-surface-600"
                              />
                              {scope}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Rate Limit (requests/min)</label>
                          <input
                            type="number"
                            value={newGpuApiKeyConfig.rateLimit}
                            onChange={(e) => setNewGpuApiKeyConfig({ ...newGpuApiKeyConfig, rateLimit: parseInt(e.target.value) || 100 })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Expires At (optional)</label>
                          <input
                            type="date"
                            value={newGpuApiKeyConfig.expiresAt}
                            onChange={(e) => setNewGpuApiKeyConfig({ ...newGpuApiKeyConfig, expiresAt: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => {
                            if (!newGpuApiKeyConfig.name) {
                              toast.error('Please enter an API key name');
                              return;
                            }
                            createApiKeyMutation.mutate({
                              ...newGpuApiKeyConfig,
                              expiresAt: newGpuApiKeyConfig.expiresAt || undefined
                            });
                            setShowCreateGpuApiKey(false);
                          }}
                          disabled={createApiKeyMutation.isPending}
                          className="flex-1 btn-primary"
                        >
                          {createApiKeyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create API Key'}
                        </button>
                        <button
                          onClick={() => setShowCreateGpuApiKey(false)}
                          className="flex-1 btn-ghost"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* API Keys List */}
                {selectedGpuInstanceForApiKey && apiKeys.length > 0 ? (
                  (() => {
                    // Filter keys by:
                    // 1. Name includes instance name (admin-created keys)
                    // 2. clientInfo.gpuContainerId or gpuContainerName matches (client dashboard keys)
                    const filteredKeys = apiKeys.filter(key => {
                      const nameMatch = key.name.includes(selectedGpuInstanceForApiKey);
                      const clientInfo = key.clientInfo as { gpuContainerId?: string; gpuContainerName?: string; source?: string } | null;
                      const clientMatch = clientInfo && (
                        clientInfo.gpuContainerId === selectedGpuInstanceForApiKey ||
                        clientInfo.gpuContainerName?.includes(selectedGpuInstanceForApiKey)
                      );
                      return nameMatch || clientMatch;
                    });
                    const totalPages = Math.ceil(filteredKeys.length / apiKeysPerPage);
                    const startIndex = (apiKeysPage - 1) * apiKeysPerPage;
                    const endIndex = startIndex + apiKeysPerPage;
                    const paginatedKeys = filteredKeys.slice(startIndex, endIndex);

                    return (
                      <>
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-surface-200">API Keys for {selectedGpuInstanceForApiKey}</h4>
                          {paginatedKeys.map((key) => {
                            const clientInfo = key.clientInfo as { source?: string; userEmail?: string; workspaceName?: string } | null;
                            const isClientDashboardKey = clientInfo?.source === 'client-dashboard';
                            return (
                      <div key={key.id} className="p-4 rounded-lg bg-surface-800/50 border border-surface-700/50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-surface-100">{key.name}</span>
                              <span className={clsx(
                                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                                key.isActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
                              )}>
                                {key.isActive ? 'Active' : 'Inactive'}
                              </span>
                              {isClientDashboardKey && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
                                  Client Dashboard
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm text-surface-400">
                              <div>
                                <span className="text-surface-500">Key Prefix:</span> {key.keyPrefix}***
                              </div>
                              <div>
                                <span className="text-surface-500">Rate Limit:</span> {key.rateLimit}/min
                              </div>
                              <div>
                                <span className="text-surface-500">Scopes:</span> {key.scopes.join(', ')}
                              </div>
                              {key.lastUsedAt && (
                                <div>
                                  <span className="text-surface-500">Last Used:</span>{' '}
                                  {formatDistanceToNow(new Date(key.lastUsedAt), { addSuffix: true })}
                                </div>
                              )}
                              {isClientDashboardKey && clientInfo?.userEmail && (
                                <div className="col-span-2">
                                  <span className="text-surface-500">Created by:</span> {clientInfo.userEmail}
                                  {clientInfo.workspaceName && ` (${clientInfo.workspaceName})`}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {key.isActive ? (
                              <button
                                onClick={() => revokeApiKeyMutation.mutate(key.id)}
                                disabled={revokeApiKeyMutation.isPending}
                                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                title="Revoke API key"
                              >
                                {revokeApiKeyMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to permanently delete this API key? This action cannot be undone.')) {
                                    deleteApiKeyMutation.mutate(key.id);
                                  }
                                }}
                                disabled={deleteApiKeyMutation.isPending}
                                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                title="Delete API key permanently"
                              >
                                {deleteApiKeyMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                            );
                          })}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between pt-4 border-t border-surface-700">
                            <div className="text-sm text-surface-400">
                              Showing {startIndex + 1}-{Math.min(endIndex, filteredKeys.length)} of {filteredKeys.length} keys
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setApiKeysPage(Math.max(1, apiKeysPage - 1))}
                                disabled={apiKeysPage === 1}
                                className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                  // Show first, last, current, and pages around current
                                  if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= apiKeysPage - 1 && page <= apiKeysPage + 1)
                                  ) {
                                    return (
                                      <button
                                        key={page}
                                        onClick={() => setApiKeysPage(page)}
                                        className={clsx(
                                          'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                                          page === apiKeysPage
                                            ? 'bg-gpu-500 text-white'
                                            : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                                        )}
                                      >
                                        {page}
                                      </button>
                                    );
                                  } else if (
                                    page === apiKeysPage - 2 ||
                                    page === apiKeysPage + 2
                                  ) {
                                    return <span key={page} className="text-surface-500">...</span>;
                                  }
                                  return null;
                                })}
                              </div>
                              <button
                                onClick={() => setApiKeysPage(Math.min(totalPages, apiKeysPage + 1))}
                                disabled={apiKeysPage === totalPages}
                                className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : selectedGpuInstanceForApiKey ? (
                  <div className="text-center py-8 text-surface-400">
                    <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No API keys yet</p>
                    <p className="text-sm text-surface-500 mt-1">Create an API key to authenticate with this instance</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Fleet Control Tab */}
      {activeTab === 'fleet' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* Cost Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full blur-2xl" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-surface-400 text-sm mb-1">EC2 Running</p>
                  <p className="text-3xl font-bold text-surface-100">
                    {fleetStatus?.runningInstances ?? 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Server className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <div className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-2xl" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-surface-400 text-sm mb-1">ECS Tasks</p>
                  <p className="text-3xl font-bold text-surface-100">
                    {ecsClusterDetails?.runningTasksCount ?? 0}
                  </p>
                  {ecsClusterDetails?.registeredContainerInstancesCount !== undefined && (
                    <p className="text-xs text-surface-500 mt-1">
                      {ecsClusterDetails.registeredContainerInstancesCount} GPU instances
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Container className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <div className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full blur-2xl" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-surface-400 text-sm mb-1">EC2 Stopped</p>
                  <p className="text-3xl font-bold text-surface-100">
                    {fleetStatus?.stoppedInstances ?? 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                  <Pause className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <div className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-red-500 to-pink-500 rounded-full blur-2xl" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-surface-400 text-sm mb-1">Hourly Cost</p>
                  <p className="text-3xl font-bold text-surface-100">
                    ${costSavings?.currentHourlyCostUsd.toFixed(2) ?? '0.00'}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <div className="card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full blur-2xl" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-surface-400 text-sm mb-1">Monthly Savings</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    ${costSavings?.monthlySavingsUsd.toFixed(0) ?? '0'}
                  </p>
                  <p className="text-xs text-surface-500 mt-1">if stopped now</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Fleet Control Panel */}
          <div className="card">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-5 h-5 text-gpu-400" />
              <h2 className="text-xl font-semibold text-surface-100">Fleet Control</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Start Fleet Button */}
              <button
                onClick={() => setConfirmAction('start')}
                disabled={isAnyMutationLoading || (fleetStatus?.stoppedInstances ?? 0) === 0}
                className={clsx(
                  'flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all duration-200',
                  fleetStatus?.stoppedInstances && fleetStatus.stoppedInstances > 0
                    ? 'border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 hover:border-emerald-500/50 cursor-pointer'
                    : 'border-surface-700 bg-surface-800/50 opacity-50 cursor-not-allowed'
                )}
              >
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Play className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-surface-100">Start Fleet</h3>
                  <p className="text-sm text-surface-400 mt-1">
                    Resume all stopped instances
                  </p>
                </div>
              </button>

              {/* Stop Fleet Button */}
              <button
                onClick={() => setConfirmAction('stop')}
                disabled={isAnyMutationLoading || (fleetStatus?.runningInstances ?? 0) === 0}
                className={clsx(
                  'flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all duration-200',
                  fleetStatus?.runningInstances && fleetStatus.runningInstances > 0
                    ? 'border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 hover:border-yellow-500/50 cursor-pointer'
                    : 'border-surface-700 bg-surface-800/50 opacity-50 cursor-not-allowed'
                )}
              >
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Pause className="w-8 h-8 text-yellow-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-surface-100">Hibernate Fleet</h3>
                  <p className="text-sm text-surface-400 mt-1">
                    Stop instances, preserve state
                  </p>
                </div>
              </button>

              {/* Terminate Fleet Button */}
              <button
                onClick={() => setConfirmAction('terminate')}
                disabled={isAnyMutationLoading || (fleetStatus?.totalInstances ?? 0) === 0}
                className={clsx(
                  'flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all duration-200',
                  fleetStatus?.totalInstances && fleetStatus.totalInstances > 0
                    ? 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20 hover:border-red-500/50 cursor-pointer'
                    : 'border-surface-700 bg-surface-800/50 opacity-50 cursor-not-allowed'
                )}
              >
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Power className="w-8 h-8 text-red-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-surface-100">Terminate Fleet</h3>
                  <p className="text-sm text-surface-400 mt-1">
                    Shutdown all instances completely
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* EC2 Instance List */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-gpu-400" />
                <h2 className="text-xl font-semibold text-surface-100">EC2 Instances</h2>
              </div>
              <span className="text-sm text-surface-400">
                {fleetStatus?.totalInstances ?? 0} total
              </span>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gpu-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !fleetStatus?.instances?.length ? (
              <div className="text-center py-12">
                <Server className="w-12 h-12 text-surface-600 mx-auto mb-4" />
                <p className="text-surface-400">No EC2 instances in fleet</p>
                <p className="text-surface-500 text-sm mt-2">
                  Deploy EC2 infrastructure and launch GPU jobs to start instances
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {fleetStatus.instances.map((instance) => (
                  <motion.div
                    key={instance.instanceId}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-gpu-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={clsx(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
                              getStateColor(instance.state)
                            )}
                          >
                            <span className={clsx('w-1.5 h-1.5 rounded-full', getStateDot(instance.state))} />
                            {instance.state}
                          </span>
                          <span className="font-mono text-sm text-surface-300">
                            {instance.instanceType}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-300">
                            EC2
                          </span>
                        </div>
                        <div className="font-mono text-sm text-gpu-400 mb-3">
                          {instance.instanceId}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {instance.publicIpAddress && (
                            <div className="flex items-center gap-2 text-surface-400">
                              <Globe className="w-4 h-4" />
                              <span className="font-mono">{instance.publicIpAddress}</span>
                            </div>
                          )}
                          {instance.privateIpAddress && (
                            <div className="flex items-center gap-2 text-surface-400">
                              <Server className="w-4 h-4" />
                              <span className="font-mono">{instance.privateIpAddress}</span>
                            </div>
                          )}
                          {instance.tags?.Project && (
                            <div className="flex items-center gap-2 text-surface-400">
                              <Cpu className="w-4 h-4" />
                              <span>{instance.tags.Project}</span>
                            </div>
                          )}
                          {instance.launchTime && (
                            <div className="flex items-center gap-2 text-surface-400">
                              <Clock className="w-4 h-4" />
                              <span>
                                {formatDistanceToNow(new Date(instance.launchTime), { addSuffix: true })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Instance Actions */}
                      <div className="flex items-center gap-2">
                        {instance.state === 'stopped' && (
                          <button
                            onClick={() => startInstanceMutation.mutate(instance.instanceId)}
                            disabled={isAnyMutationLoading}
                            className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                            title="Start instance"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        {instance.state === 'running' && (
                          <button
                            onClick={() => stopInstanceMutation.mutate(instance.instanceId)}
                            disabled={isAnyMutationLoading}
                            className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
                            title="Stop instance"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        {instance.state === 'running' && (
                          <button
                            onClick={() => hibernateInstanceMutation.mutate(instance.instanceId)}
                            disabled={isAnyMutationLoading}
                            className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
                            title="Hibernate instance (preserves memory state)"
                          >
                            <Moon className="w-4 h-4" />
                          </button>
                        )}
                        {(instance.state === 'running' || instance.state === 'stopped') && (
                          <button
                            onClick={() => terminateInstanceMutation.mutate(instance.instanceId)}
                            disabled={isAnyMutationLoading}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            title="Terminate instance"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* ECS Tasks & Cluster Section */}
          {deploymentStatus?.ecs?.status === 'deployed' && (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Container className="w-5 h-5 text-blue-400" />
                  <h2 className="text-xl font-semibold text-surface-100">ECS Cluster</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-surface-400">
                    {ecsClusterDetails?.registeredContainerInstancesCount ?? 0} instances
                  </span>
                  <span className="text-surface-600">•</span>
                  <span className="text-sm text-emerald-400">
                    {ecsClusterDetails?.runningTasksCount ?? 0} tasks
                  </span>
                  <button
                    onClick={() => refetchEcsCluster()}
                    className="btn-ghost p-2 ml-2"
                    title="Refresh"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Quick Scaling Controls */}
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-blue-600/10 to-cyan-600/10 border border-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-surface-200 mb-1">Quick Scale</h3>
                    <p className="text-xs text-surface-500">Adjust GPU instance count</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEcsDesiredCapacity(0);
                        setConfirmAction('scale-ecs');
                      }}
                      disabled={isAnyMutationLoading}
                      className="px-3 py-1.5 rounded-lg bg-surface-800 hover:bg-surface-700 text-surface-300 text-sm transition-colors"
                    >
                      Scale to 0
                    </button>
                    <button
                      onClick={() => {
                        setEcsDesiredCapacity(1);
                        setConfirmAction('scale-ecs');
                      }}
                      disabled={isAnyMutationLoading}
                      className="px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 text-sm transition-colors"
                    >
                      Scale to 1
                    </button>
                    <button
                      onClick={() => {
                        setEcsDesiredCapacity(2);
                        setConfirmAction('scale-ecs');
                      }}
                      disabled={isAnyMutationLoading}
                      className="px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 text-sm transition-colors"
                    >
                      Scale to 2
                    </button>
                    <div className="flex items-center gap-1 ml-2">
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={ecsDesiredCapacity}
                        onChange={(e) => setEcsDesiredCapacity(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1.5 rounded-lg bg-surface-900 border border-surface-700 text-surface-100 text-sm text-center"
                      />
                      <button
                        onClick={() => setConfirmAction('scale-ecs')}
                        disabled={isAnyMutationLoading}
                        className="btn-primary px-3 py-1.5 text-sm"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ECS Tasks List */}
              {ecsClusterDetails?.tasks && ecsClusterDetails.tasks.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-surface-300">Running Tasks</h3>
                    {ecsClusterDetails.tasks.some(t => t.lastStatus === 'PENDING') && (
                      <button
                        onClick={() => setConfirmAction('cleanup-stuck-tasks')}
                        disabled={isAnyMutationLoading}
                        className="px-3 py-1.5 rounded-lg bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-300 text-xs font-medium transition-colors flex items-center gap-2"
                        title="Stop all pending tasks"
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Clean Up Stuck Tasks ({ecsClusterDetails.tasks.filter(t => t.lastStatus === 'PENDING').length})
                      </button>
                    )}
                  </div>

                  {/* Warning banner for stuck tasks */}
                  {ecsClusterDetails.tasks.some(t => t.lastStatus === 'PENDING') && 
                   (ecsClusterDetails.registeredContainerInstancesCount === 0) && (
                    <div className="p-4 rounded-xl bg-yellow-600/10 border border-yellow-600/30 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-300 mb-1">
                            Tasks Stuck in PENDING State
                          </h4>
                          <p className="text-xs text-yellow-400/80 mb-2">
                            {ecsClusterDetails.tasks.filter(t => t.lastStatus === 'PENDING').length} task(s) are pending but there are no container instances available to run them.
                          </p>
                          <p className="text-xs text-surface-400">
                            <strong>Solution:</strong> Either scale up the cluster to provide container instances, or clean up the stuck tasks to stop incurring costs.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {ecsClusterDetails.tasks.map((task) => (
                    <div
                      key={task.taskArn}
                      className="relative p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-blue-500/30 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={clsx(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
                              task.lastStatus === 'RUNNING' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                              task.lastStatus === 'PENDING' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                              task.lastStatus === 'STOPPED' ? 'bg-surface-600/50 text-surface-300 border-surface-500/30' :
                              'bg-blue-500/20 text-blue-300 border-blue-500/30'
                            )}>
                              <span className={clsx(
                                'w-1.5 h-1.5 rounded-full',
                                task.lastStatus === 'RUNNING' ? 'bg-emerald-400 animate-pulse' :
                                task.lastStatus === 'PENDING' ? 'bg-yellow-400 animate-pulse' :
                                'bg-surface-400'
                              )} />
                              {task.lastStatus}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
                              ECS Task
                            </span>
                            <span className="font-mono text-sm text-surface-400">
                              {task.taskArn.split('/').pop()?.slice(0, 12)}...
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {task.cpu && (
                              <div className="flex items-center gap-2 text-surface-400">
                                <Cpu className="w-4 h-4" />
                                <span>{task.cpu} CPU</span>
                              </div>
                            )}
                            {task.memory && (
                              <div className="flex items-center gap-2 text-surface-400">
                                <Box className="w-4 h-4" />
                                <span>{task.memory} MB</span>
                              </div>
                            )}
                            {task.launchType && (
                              <div className="flex items-center gap-2 text-surface-400">
                                <Layers className="w-4 h-4" />
                                <span>{task.launchType}</span>
                              </div>
                            )}
                            {task.startedAt && (
                              <div className="flex items-center gap-2 text-surface-400">
                                <Clock className="w-4 h-4" />
                                <span>
                                  {formatDistanceToNow(new Date(task.startedAt), { addSuffix: true })}
                                </span>
                              </div>
                            )}
                          </div>
                          {task.stoppedReason && (
                            <div className="mt-2 text-xs text-red-400">
                              Stopped: {task.stoppedReason}
                            </div>
                          )}
                        </div>

                        {/* Task Actions */}
                        <div className="flex items-center gap-2">
                          {(task.lastStatus === 'RUNNING' || task.lastStatus === 'PENDING') && (
                            <button
                              onClick={() => {
                                setSelectedTask(task);
                                setSelectedClusterForTaskOp(deploymentStatus?.ecs?.ecsClusterName || null);
                                setConfirmAction('stop-ecs-task');
                              }}
                              disabled={isAnyMutationLoading}
                              className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                              title="Stop task"
                            >
                              <Square className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-surface-400">
                  <Container className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No ECS tasks running</p>
                  <p className="text-sm text-surface-500 mt-1">
                    {ecsClusterDetails?.registeredContainerInstancesCount 
                      ? 'Deploy GPU workloads to run ECS tasks'
                      : 'Scale up the cluster first to run tasks'}
                  </p>
                  {ecsClusterDetails?.registeredContainerInstancesCount ? (
                    <button
                      onClick={() => setConfirmAction('run-ecs-task')}
                      disabled={isAnyMutationLoading}
                      className="btn-primary mt-4"
                    >
                      <Plus className="w-4 h-4" />
                      Run GPU Task
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* GPU Containers Tab */}
      {activeTab === 'gpu-containers' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* Sub-tabs */}
          <div className="flex gap-2 border-b border-surface-700">
            <button
              onClick={() => setGpuContainersSubTab('containers')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                gpuContainersSubTab === 'containers'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Container className="w-4 h-4" />
              Containers
            </button>
            <button
              onClick={() => setGpuContainersSubTab('topology')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                gpuContainersSubTab === 'topology'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Layers className="w-4 h-4" />
              Topology
            </button>
            <button
              onClick={() => setGpuContainersSubTab('network')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                gpuContainersSubTab === 'network'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Globe className="w-4 h-4" />
              Network
            </button>
            <button
              onClick={() => setGpuContainersSubTab('api-keys')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                gpuContainersSubTab === 'api-keys'
                  ? 'text-gpu-400 border-gpu-500'
                  : 'text-surface-400 border-transparent hover:text-surface-200'
              )}
            >
              <Settings2 className="w-4 h-4" />
              API Keys
            </button>
          </div>

          {/* Containers Sub-tab */}
          {gpuContainersSubTab === 'containers' && (
            <div className="space-y-6">
              {/* Header with Create Container Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-surface-100">GPU Containers</h2>
                  <p className="text-sm text-surface-400 mt-1">Manage your containerized GPU workloads</p>
                </div>
                <button
                  onClick={() => setShowCreateContainer(!showCreateContainer)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {showCreateContainer ? 'Cancel' : 'Create Container'}
                </button>
              </div>

              {/* Create New Container Form */}
              {showCreateContainer && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="card bg-gradient-to-br from-blue-600/10 to-cyan-600/10 border-blue-500/20"
                >
                  <h3 className="text-lg font-semibold text-surface-100 mb-4">Create New Container</h3>
                  
                  <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                    <p className="text-xs text-blue-300">
                      🌐 Each GPU container automatically gets its own dedicated network infrastructure (VPC, subnets, security groups, and routing). 
                      The network is deployed first, then your GPU instances are launched.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Container Name</label>
                        <input
                          type="text"
                          value={newContainerConfig.clusterName}
                          onChange={(e) => setNewContainerConfig({ ...newContainerConfig, clusterName: e.target.value })}
                          placeholder="my-gpu-container"
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Instance Type</label>
                        <select
                          value={newContainerConfig.instanceType}
                          onChange={(e) => setNewContainerConfig({ ...newContainerConfig, instanceType: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        >
                          <option value="g4dn.xlarge">g4dn.xlarge (T4 16GB)</option>
                          <option value="g4dn.2xlarge">g4dn.2xlarge (T4 16GB)</option>
                          <option value="g5.xlarge">g5.xlarge (A10G 24GB)</option>
                          <option value="g5.2xlarge">g5.2xlarge (A10G 24GB)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Max Instances</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={newContainerConfig.maxInstances}
                          onChange={(e) => setNewContainerConfig({ ...newContainerConfig, maxInstances: parseInt(e.target.value) || 2 })}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Initial Capacity</label>
                        <input
                          type="number"
                          min="0"
                          max={newContainerConfig.maxInstances}
                          value={newContainerConfig.desiredCount}
                          onChange={(e) => setNewContainerConfig({ ...newContainerConfig, desiredCount: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                    </div>
                    
                    {newContainerConfig.instanceType.startsWith('p3') && (
                      <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-yellow-300">p3 instances do not include NVMe instance storage. Only EBS volumes are available.</p>
                      </div>
                    )}

                    <label className="flex items-center gap-2 text-sm text-surface-300">
                      <input
                        type="checkbox"
                        checked={newContainerConfig.useSpot}
                        onChange={(e) => setNewContainerConfig({ ...newContainerConfig, useSpot: e.target.checked })}
                        className="w-4 h-4 rounded border-surface-600"
                      />
                      Use Spot Instances (up to 90% savings)
                    </label>

                    {/* Auto Scale-Down Settings */}
                    <div className="border-t border-surface-700 pt-4 mt-4">
                      <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                        <Moon className="w-4 h-4" />
                        Auto Scale-Down Settings
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Grace Period (seconds)</label>
                          <input
                            type="number"
                            min="30"
                            max="3600"
                            value={Math.round(newContainerConfig.autoScaleDownGracePeriodMs / 1000)}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, autoScaleDownGracePeriodMs: (parseInt(e.target.value) || 60) * 1000 })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                          <p className="text-xs text-surface-400 mt-1">Time to wait before scaling down idle cluster</p>
                        </div>
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Check Interval (seconds)</label>
                          <input
                            type="number"
                            min="30"
                            max="600"
                            value={Math.round(newContainerConfig.autoScaleDownCheckIntervalMs / 1000)}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, autoScaleDownCheckIntervalMs: (parseInt(e.target.value) || 60) * 1000 })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                          <p className="text-xs text-surface-400 mt-1">How often to check if cluster is idle</p>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Container Settings Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowAdvancedContainer(!showAdvancedContainer)}
                      className="flex items-center gap-2 text-sm text-accent hover:text-accent/80 transition-colors mt-2"
                    >
                      {showAdvancedContainer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {showAdvancedContainer ? 'Hide' : 'Show'} Advanced Settings
                    </button>

                    {showAdvancedContainer && (
                      <div className="space-y-4 border-t border-surface-700 pt-4">
                        {/* Docker Image */}
                        <div>
                          <label className="block text-sm text-surface-300 mb-2 flex items-center gap-1">
                            <Container className="w-4 h-4" /> Docker Image
                          </label>
                          <select
                            value={newContainerConfig.dockerImage}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, dockerImage: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          >
                            <option value="">Default CUDA Image (nvidia/cuda:12.2.0-base-ubuntu22.04)</option>
                            <option value="pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime">PyTorch 2.1 (CUDA 12.1)</option>
                            <option value="tensorflow/tensorflow:2.14.0-gpu">TensorFlow 2.14 (GPU)</option>
                            <option value="huggingface/transformers-pytorch-gpu:latest">HuggingFace Transformers (PyTorch GPU)</option>
                            <option value="nvcr.io/nvidia/pytorch:23.10-py3">NVIDIA PyTorch NGC (Optimized)</option>
                            <option value="nvcr.io/nvidia/tensorflow:23.10-tf2-py3">NVIDIA TensorFlow NGC (Optimized)</option>
                            <option value="custom">Custom Image...</option>
                          </select>
                          {newContainerConfig.dockerImage === 'custom' && (
                            <input
                              type="text"
                              placeholder="e.g., my-registry.com/my-image:latest"
                              onChange={(e) => setNewContainerConfig({ ...newContainerConfig, dockerImage: e.target.value || 'custom' })}
                              className="w-full mt-2 px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                            />
                          )}
                        </div>

                        {/* Startup Command */}
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Startup Command (optional)</label>
                          <input
                            type="text"
                            value={newContainerConfig.startupCommand}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, startupCommand: e.target.value })}
                            placeholder='e.g., python train.py --epochs 100'
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>

                        {/* Environment Variables */}
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Environment Variables (KEY=VALUE, one per line)</label>
                          <textarea
                            value={newContainerConfig.environmentVariables}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, environmentVariables: e.target.value })}
                            placeholder={'BATCH_SIZE=32\nLEARNING_RATE=0.001\nWANDB_API_KEY=your-key'}
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100 font-mono text-xs"
                          />
                        </div>

                        {/* Task Resources */}
                        <div>
                          <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                            <Cpu className="w-4 h-4" /> Task Resources
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Task CPU (units)</label>
                              <select
                                value={newContainerConfig.taskCpu}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, taskCpu: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value="1024">1 vCPU (1024)</option>
                                <option value="2048">2 vCPUs (2048)</option>
                                <option value="4096">4 vCPUs (4096)</option>
                                <option value="8192">8 vCPUs (8192)</option>
                                <option value="16384">16 vCPUs (16384)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Task Memory (MiB)</label>
                              <select
                                value={newContainerConfig.taskMemory}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, taskMemory: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value="8192">8 GB</option>
                                <option value="16384">16 GB</option>
                                <option value="32768">32 GB</option>
                                <option value="65536">64 GB</option>
                                <option value="131072">128 GB</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Port Mapping */}
                        <div>
                          <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                            <Network className="w-4 h-4" /> Port Mapping
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Container Port</label>
                              <input
                                type="number"
                                min="1"
                                max="65535"
                                value={newContainerConfig.containerPort}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, containerPort: parseInt(e.target.value) || 8080 })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Host Port (0 = dynamic)</label>
                              <input
                                type="number"
                                min="0"
                                max="65535"
                                value={newContainerConfig.hostPort}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, hostPort: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Health Check */}
                        <div>
                          <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                            <Activity className="w-4 h-4" /> Health Check
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Health Check Path (leave empty to skip)</label>
                              <input
                                type="text"
                                value={newContainerConfig.healthCheckPath}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, healthCheckPath: e.target.value })}
                                placeholder="/health"
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              />
                            </div>
                            {newContainerConfig.healthCheckPath && (
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm text-surface-300 mb-2">Interval (s)</label>
                                  <input
                                    type="number"
                                    min="5"
                                    max="300"
                                    value={newContainerConfig.healthCheckIntervalSeconds}
                                    onChange={(e) => setNewContainerConfig({ ...newContainerConfig, healthCheckIntervalSeconds: parseInt(e.target.value) || 30 })}
                                    className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-surface-300 mb-2">Timeout (s)</label>
                                  <input
                                    type="number"
                                    min="2"
                                    max="120"
                                    value={newContainerConfig.healthCheckTimeoutSeconds}
                                    onChange={(e) => setNewContainerConfig({ ...newContainerConfig, healthCheckTimeoutSeconds: parseInt(e.target.value) || 5 })}
                                    className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-surface-300 mb-2">Retries</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={newContainerConfig.healthCheckRetries}
                                    onChange={(e) => setNewContainerConfig({ ...newContainerConfig, healthCheckRetries: parseInt(e.target.value) || 3 })}
                                    className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Logging */}
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">CloudWatch Log Retention (days)</label>
                          <select
                            value={newContainerConfig.logRetentionDays}
                            onChange={(e) => setNewContainerConfig({ ...newContainerConfig, logRetentionDays: parseInt(e.target.value) || 30 })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          >
                            <option value={1}>1 day</option>
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                            <option value={365}>1 year</option>
                          </select>
                        </div>

                        {/* Platform Lifecycle */}
                        <div className="border-t border-surface-700 pt-4">
                          <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Lifecycle & Cost Controls
                          </h4>

                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Idle Timeout (minutes, 0 = disabled)</label>
                              <select
                                value={newContainerConfig.idleTimeoutMinutes}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, idleTimeoutMinutes: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value={0}>Disabled</option>
                                <option value={5}>5 minutes</option>
                                <option value={10}>10 minutes</option>
                                <option value={15}>15 minutes</option>
                                <option value={30}>30 minutes</option>
                                <option value={60}>1 hour</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-surface-300 mb-2">Max Cost Limit (USD, 0 = no limit)</label>
                              <select
                                value={newContainerConfig.maxCostUsd}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, maxCostUsd: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                              >
                                <option value={0}>No limit</option>
                                <option value={5}>$5</option>
                                <option value={10}>$10</option>
                                <option value={25}>$25</option>
                                <option value={50}>$50</option>
                                <option value={100}>$100</option>
                                <option value={250}>$250</option>
                                <option value={500}>$500</option>
                              </select>
                            </div>
                          </div>

                          <label className="flex items-center gap-2 text-sm text-surface-300 mb-2">
                            <input
                              type="checkbox"
                              checked={newContainerConfig.requireHeartbeat}
                              onChange={(e) => setNewContainerConfig({ ...newContainerConfig, requireHeartbeat: e.target.checked })}
                              className="w-4 h-4 rounded border-surface-600"
                            />
                            <Zap className="w-3.5 h-3.5" /> Require Heartbeat (terminate if client stops responding)
                          </label>
                        </div>

                        {/* Notifications */}
                        <div className="border-t border-surface-700 pt-4">
                          <h4 className="text-sm font-medium text-surface-200 mb-3 flex items-center gap-2">
                            <Bell className="w-4 h-4" /> Notifications
                          </h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-surface-300">
                              <input
                                type="checkbox"
                                checked={newContainerConfig.notifyOnComplete}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, notifyOnComplete: e.target.checked })}
                                className="w-4 h-4 rounded border-surface-600"
                              />
                              Notify on job completion
                            </label>
                            <label className="flex items-center gap-2 text-sm text-surface-300">
                              <input
                                type="checkbox"
                                checked={newContainerConfig.notifyOnFailure}
                                onChange={(e) => setNewContainerConfig({ ...newContainerConfig, notifyOnFailure: e.target.checked })}
                                className="w-4 h-4 rounded border-surface-600"
                              />
                              Notify on failure
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => {
                          if (!newContainerConfig.clusterName) {
                            toast.error('Please enter a container name');
                            return;
                          }
                          const envVars: Record<string, string> = {};
                          if (newContainerConfig.environmentVariables.trim()) {
                            newContainerConfig.environmentVariables.trim().split('\n').forEach(line => {
                              const eqIdx = line.indexOf('=');
                              if (eqIdx > 0) envVars[line.substring(0, eqIdx).trim()] = line.substring(eqIdx + 1).trim();
                            });
                          }
                          deployEcsMutation.mutate({
                            clusterName: newContainerConfig.clusterName,
                            instanceType: newContainerConfig.instanceType,
                            maxInstances: newContainerConfig.maxInstances,
                            desiredCount: newContainerConfig.desiredCount,
                            useSpot: newContainerConfig.useSpot,
                            autoScaleDownGracePeriodMs: newContainerConfig.autoScaleDownGracePeriodMs,
                            autoScaleDownCheckIntervalMs: newContainerConfig.autoScaleDownCheckIntervalMs,
                            ...(newContainerConfig.dockerImage && newContainerConfig.dockerImage !== 'custom' && { dockerImage: newContainerConfig.dockerImage }),
                            ...(newContainerConfig.startupCommand && { startupCommand: newContainerConfig.startupCommand }),
                            ...(Object.keys(envVars).length > 0 && { environmentVariables: envVars }),
                            containerPort: newContainerConfig.containerPort,
                            hostPort: newContainerConfig.hostPort,
                            taskCpu: newContainerConfig.taskCpu,
                            taskMemory: newContainerConfig.taskMemory,
                            ...(newContainerConfig.healthCheckPath && {
                              healthCheckPath: newContainerConfig.healthCheckPath,
                              healthCheckIntervalSeconds: newContainerConfig.healthCheckIntervalSeconds,
                              healthCheckTimeoutSeconds: newContainerConfig.healthCheckTimeoutSeconds,
                              healthCheckRetries: newContainerConfig.healthCheckRetries,
                            }),
                            logRetentionDays: newContainerConfig.logRetentionDays,
                            idleTimeoutMinutes: newContainerConfig.idleTimeoutMinutes,
                            requireHeartbeat: newContainerConfig.requireHeartbeat,
                            maxCostUsd: newContainerConfig.maxCostUsd,
                            notifyOnComplete: newContainerConfig.notifyOnComplete,
                            notifyOnFailure: newContainerConfig.notifyOnFailure,
                          });
                        }}
                        disabled={deployEcsMutation.isPending}
                        className="flex-1 btn-primary"
                      >
                        {deployEcsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Container'}
                      </button>
                      <button
                        onClick={() => setShowCreateContainer(false)}
                        className="flex-1 btn-ghost"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* List of Containers */}
              <div className="space-y-4">
                {allEcsClusters && allEcsClusters.clusters && allEcsClusters.clusters.length > 0 ? (
                  <>
                    {/* Date Range and Page Size Filters */}
                    <div className="flex items-center gap-4 flex-wrap p-4 rounded-lg bg-surface-800/30 border border-surface-700/50">
                      {/* Date Range Filter */}
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-surface-400" />
                        <span className="text-sm text-surface-400">Created:</span>
                        <input
                          type="date"
                          value={containersDateFrom}
                          onChange={(e) => {
                            setContainersDateFrom(e.target.value);
                            setContainersPage(1);
                          }}
                          className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                          placeholder="From"
                        />
                        <span className="text-surface-500">to</span>
                        <input
                          type="date"
                          value={containersDateTo}
                          onChange={(e) => {
                            setContainersDateTo(e.target.value);
                            setContainersPage(1);
                          }}
                          className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                          placeholder="To"
                        />
                        {(containersDateFrom || containersDateTo) && (
                          <button
                            onClick={() => {
                              setContainersDateFrom('');
                              setContainersDateTo('');
                              setContainersPage(1);
                            }}
                            className="px-2 py-1 rounded-lg text-xs bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {/* Page Size Selector */}
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-sm text-surface-400">Per page:</span>
                        <select
                          value={containersPerPage}
                          onChange={(e) => {
                            setContainersPerPage(Number(e.target.value));
                            setContainersPage(1);
                          }}
                          className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors cursor-pointer"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>

                    {(() => {
                      // Note: ECS clusters don't have createdAt in the API response, so date filtering is limited
                      // We'll keep the UI for consistency but it won't filter unless the API provides timestamps
                      let filteredClusters = allEcsClusters.clusters;

                      // Calculate pagination
                      const totalContainersPages = Math.ceil(filteredClusters.length / containersPerPage);
                      const containersStartIndex = (containersPage - 1) * containersPerPage;
                      const containersEndIndex = containersStartIndex + containersPerPage;
                      const paginatedClusters = filteredClusters.slice(containersStartIndex, containersEndIndex);

                      return (
                        <>
                          {paginatedClusters.map((cluster) => (
                    <div key={cluster.clusterArn} className="card">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                            <Layers className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-surface-100">{cluster.clusterName}</h3>
                            <p className="text-sm text-surface-400">
                              {cluster.registeredContainerInstancesCount} instances • {cluster.runningTasksCount} running tasks
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => refetchAllClusters()}
                            className="btn-ghost p-2"
                            title="Refresh"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedClusterToDelete(cluster.clusterName);
                              setConfirmAction('delete-ecs-cluster');
                            }}
                            disabled={isAnyMutationLoading}
                            className="btn-ghost text-red-400 hover:bg-red-500/10 p-2"
                            title="Delete container"
                          >
                            {deleteEcsClusterMutation.isPending && selectedClusterToDelete === cluster.clusterName ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                          <span className={clsx(
                            'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium',
                            cluster.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
                          )}>
                            <span className={clsx(
                              'w-1.5 h-1.5 rounded-full',
                              cluster.status === 'ACTIVE' && 'bg-emerald-400 animate-pulse'
                            )} />
                            {cluster.status}
                          </span>
                        </div>
                      </div>

                      {/* Container Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="text-xs text-surface-400">GPU Instances</div>
                          <div className="text-2xl font-bold text-surface-100 mt-1">
                            {cluster.registeredContainerInstancesCount}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="text-xs text-surface-400">Running Tasks</div>
                          <div className="text-2xl font-bold text-emerald-400 mt-1">
                            {cluster.runningTasksCount}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="text-xs text-surface-400">Pending Tasks</div>
                          <div className="text-2xl font-bold text-yellow-400 mt-1">
                            {cluster.pendingTasksCount}
                          </div>
                        </div>
                      </div>

                      {/* Cluster Scaling Controls */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-blue-600/10 to-cyan-600/10 border border-blue-500/20">
                        <div className="flex items-center gap-2 mb-3">
                          <BarChart3 className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-surface-200">Scale Container</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              const current = containerScaleValues[cluster.clusterName] ?? cluster.registeredContainerInstancesCount;
                              setContainerScaleValues({
                                ...containerScaleValues,
                                [cluster.clusterName]: Math.max(0, current - 1)
                              });
                            }}
                            disabled={isAnyMutationLoading || (containerScaleValues[cluster.clusterName] ?? cluster.registeredContainerInstancesCount) <= 0}
                            className="p-2 rounded-lg bg-surface-800 hover:bg-surface-700 transition-colors disabled:opacity-50"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <div className="flex-1 text-center">
                            <div className="text-2xl font-bold text-surface-100">
                              {containerScaleValues[cluster.clusterName] ?? cluster.registeredContainerInstancesCount}
                            </div>
                            <div className="text-xs text-surface-400">GPU instances</div>
                          </div>
                          <button
                            onClick={() => {
                              const current = containerScaleValues[cluster.clusterName] ?? cluster.registeredContainerInstancesCount;
                              setContainerScaleValues({
                                ...containerScaleValues,
                                [cluster.clusterName]: current + 1
                              });
                            }}
                            disabled={isAnyMutationLoading}
                            className="p-2 rounded-lg bg-surface-800 hover:bg-surface-700 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedContainerForScaling(cluster.clusterName);
                              setConfirmAction('scale-ecs');
                            }}
                            disabled={isAnyMutationLoading}
                            className="btn-primary px-4"
                          >
                            {scaleEcsMutation.isPending && selectedContainerForScaling === cluster.clusterName ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Scale'
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-surface-500 mt-2">
                          Current: {cluster.registeredContainerInstancesCount} instances
                        </p>
                      </div>

                      {/* Instance Management Section */}
                      {cluster.registeredContainerInstancesCount > 0 && (
                        <div className="mt-4 border-t border-surface-700 pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <button
                              onClick={() => setExpandedContainer(expandedContainer === cluster.clusterName ? null : cluster.clusterName)}
                              className="flex items-center gap-2 text-sm font-medium text-surface-200 hover:text-surface-100"
                            >
                              <Server className="w-4 h-4" />
                              Manage Instances ({cluster.registeredContainerInstancesCount})
                              <motion.div
                                animate={{ rotate: expandedContainer === cluster.clusterName ? 90 : 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </motion.div>
                            </button>
                            {expandedContainer === cluster.clusterName && selectedInstances.size > 0 && (
                              <button
                                onClick={() => setConfirmAction('terminate-instances-bulk')}
                                disabled={terminateBulkInstancesMutation.isPending}
                                className="btn-ghost text-red-400 hover:bg-red-500/10 text-sm flex items-center gap-2"
                              >
                                {terminateBulkInstancesMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Power className="w-4 h-4" />
                                )}
                                Terminate Selected ({selectedInstances.size})
                              </button>
                            )}
                          </div>

                          {expandedContainer === cluster.clusterName && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="space-y-2"
                            >
                              {/* Fetch cluster details to get instances */}
                              <InstanceList
                                clusterName={cluster.clusterName}
                                selectedInstances={selectedInstances}
                                setSelectedInstances={setSelectedInstances}
                                onTerminate={(instanceId) => {
                                  setInstanceToDestroy(instanceId);
                                  setSelectedContainerForScaling(cluster.clusterName);
                                  setConfirmAction('terminate-instance');
                                }}
                              />
                            </motion.div>
                          )}
                        </div>
                      )}

                      {/* Task Management Section */}
                      <div className="mt-4 border-t border-surface-700 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <button
                            onClick={() => setExpandedTasksContainer(expandedTasksContainer === cluster.clusterName ? null : cluster.clusterName)}
                            className="flex items-center gap-2 text-sm font-medium text-surface-200 hover:text-surface-100"
                          >
                            <Container className="w-4 h-4" />
                            Manage Tasks ({cluster.runningTasksCount} running, {cluster.pendingTasksCount} pending)
                            <motion.div
                              animate={{ rotate: expandedTasksContainer === cluster.clusterName ? 90 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </motion.div>
                          </button>
                          {cluster.registeredContainerInstancesCount > 0 && (
                            <button
                              onClick={() => {
                                setSelectedClusterForTaskOp(cluster.clusterName);
                                setConfirmAction('run-ecs-task');
                              }}
                              disabled={isAnyMutationLoading}
                              className="btn-primary text-sm flex items-center gap-2"
                            >
                              {runEcsTaskMutation.isPending && selectedClusterForTaskOp === cluster.clusterName ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                              Run Task
                            </button>
                          )}
                        </div>

                        {expandedTasksContainer === cluster.clusterName && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-2"
                          >
                            {(cluster.runningTasksCount > 0 || cluster.pendingTasksCount > 0) ? (
                              <TaskList
                                clusterName={cluster.clusterName}
                                isLoading={stopEcsTaskMutation.isPending || cleanupStuckTasksMutation.isPending}
                                onStopTask={(clusterName, task) => {
                                  setSelectedTask(task);
                                  setSelectedClusterForTaskOp(clusterName);
                                  setConfirmAction('stop-ecs-task');
                                }}
                                onCleanupStuckTasks={(clusterName, tasks) => {
                                  setSelectedClusterForTaskOp(clusterName);
                                  setPendingTasksToCleanup(tasks);
                                  setConfirmAction('cleanup-stuck-tasks');
                                }}
                              />
                            ) : (
                              <div className="text-center py-6 text-surface-400">
                                <Container className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No tasks running</p>
                                {cluster.registeredContainerInstancesCount === 0 && (
                                  <p className="text-xs text-surface-500 mt-1">Scale up to run tasks</p>
                                )}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Pagination Controls */}
                  {totalContainersPages > 1 && (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-surface-800/30 border border-surface-700/50">
                      <div className="text-sm text-surface-400">
                        Showing {containersStartIndex + 1}-{Math.min(containersEndIndex, filteredClusters.length)} of {filteredClusters.length} containers
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setContainersPage(Math.max(1, containersPage - 1))}
                          disabled={containersPage === 1}
                          className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalContainersPages }, (_, i) => i + 1).map((page) => {
                            // Show first, last, current, and pages around current
                            if (
                              page === 1 ||
                              page === totalContainersPages ||
                              (page >= containersPage - 1 && page <= containersPage + 1)
                            ) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => setContainersPage(page)}
                                  className={clsx(
                                    'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                                    page === containersPage
                                      ? 'bg-gpu-500 text-white'
                                      : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                                  )}
                                >
                                  {page}
                                </button>
                              );
                            } else if (
                              page === containersPage - 2 ||
                              page === containersPage + 2
                            ) {
                              return <span key={page} className="text-surface-500">...</span>;
                            }
                            return null;
                          })}
                        </div>
                        <button
                          onClick={() => setContainersPage(Math.min(totalContainersPages, containersPage + 1))}
                          disabled={containersPage === totalContainersPages}
                          className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
                );
              })()}
                </>) : (
                  <div className="card text-center py-12">
                    <Container className="w-16 h-16 mx-auto mb-4 text-surface-600" />
                    <h3 className="text-lg font-semibold text-surface-100 mb-2">No Containers Yet</h3>
                    <p className="text-sm text-surface-400 mb-4">
                      Create your first GPU container to get started
                    </p>
                    <button
                      onClick={() => setShowCreateContainer(true)}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Container
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Topology Sub-tab */}
          {gpuContainersSubTab === 'topology' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-surface-100">Infrastructure Topology</h2>
                  <p className="text-sm text-surface-400 mt-1">Visual representation of containers, instances, and their connections</p>
                </div>
                <button
                  onClick={() => refetchAllClusters()}
                  className="btn-ghost flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {isLoadingClusters ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gpu-400" />
                </div>
              ) : !allEcsClusters?.clusters || allEcsClusters.clusters.length === 0 ? (
                <div className="card text-center py-12">
                  <Layers className="w-16 h-16 mx-auto mb-4 text-surface-600" />
                  <h3 className="text-lg font-medium text-surface-300 mb-2">No Containers Found</h3>
                  <p className="text-sm text-surface-400 mb-6">
                    Create a GPU container to see its topology visualization
                  </p>
                  <button
                    onClick={() => setGpuContainersSubTab('containers')}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Container
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  {allEcsClusters.clusters.map((cluster) => (
                    <TopologyView key={cluster.clusterArn} cluster={cluster} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Network Sub-tab - Using NetworkPanel Component */}
          {gpuContainersSubTab === 'network' && (
            <NetworkPanel 
              mode="containers" 
              onRefresh={() => {
                refetchAllClusters();
                queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
              }}
            />
          )}

          {/* API Keys Sub-tab */}
          {gpuContainersSubTab === 'api-keys' && (
            <div className="space-y-6">
              {/* Client Dashboard Keys Section */}
              {(() => {
                const clientDashboardKeys = apiKeys.filter(key => {
                  const clientInfo = key.clientInfo as { source?: string } | null;
                  return clientInfo?.source === 'client-dashboard';
                });
                
                if (clientDashboardKeys.length === 0) return null;
                
                return (
                  <div className="card border-blue-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Key className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-surface-100">Client Dashboard API Keys</h3>
                        <p className="text-sm text-surface-400">Keys created by client dashboard users</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {clientDashboardKeys.map((key) => {
                        const clientInfo = key.clientInfo as { userEmail?: string; workspaceName?: string; gpuContainerName?: string } | null;
                        return (
                          <div key={key.id} className="p-4 rounded-lg bg-surface-800/50 border border-blue-500/20">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-surface-100">{key.name}</span>
                                  <span className={clsx(
                                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                                    key.isActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
                                  )}>
                                    {key.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm text-surface-400">
                                  <div>
                                    <span className="text-surface-500">Key Prefix:</span> {key.keyPrefix}***
                                  </div>
                                  <div>
                                    <span className="text-surface-500">Rate Limit:</span> {key.rateLimit}/min
                                  </div>
                                  {clientInfo?.userEmail && (
                                    <div>
                                      <span className="text-surface-500">User:</span> {clientInfo.userEmail}
                                    </div>
                                  )}
                                  {clientInfo?.workspaceName && (
                                    <div>
                                      <span className="text-surface-500">Workspace:</span> {clientInfo.workspaceName}
                                    </div>
                                  )}
                                  {clientInfo?.gpuContainerName && (
                                    <div>
                                      <span className="text-surface-500">Container:</span> {clientInfo.gpuContainerName}
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-surface-500">Created:</span> {formatDistanceToNow(new Date(key.createdAt), { addSuffix: true })}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {key.isActive ? (
                                  <button
                                    onClick={() => revokeApiKeyMutation.mutate(key.id)}
                                    disabled={revokeApiKeyMutation.isPending}
                                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                    title="Revoke API key"
                                  >
                                    {revokeApiKeyMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <XCircle className="w-4 h-4" />
                                    )}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      if (confirm('Are you sure you want to permanently delete this API key? This action cannot be undone.')) {
                                        deleteApiKeyMutation.mutate(key.id);
                                      }
                                    }}
                                    disabled={deleteApiKeyMutation.isPending}
                                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                    title="Delete API key permanently"
                                  >
                                    {deleteApiKeyMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Container Selection */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings2 className="w-5 h-5 text-gpu-400" />
                    <h3 className="text-lg font-semibold text-surface-100">Container API Keys</h3>
                  </div>
                  {selectedContainerForApiKey && (
                    <button
                      onClick={() => setShowCreateApiKey(true)}
                      className="btn-primary text-sm flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create API Key
                    </button>
                  )}
                </div>

                {/* Container Selector */}
                {allEcsClusters && allEcsClusters.clusters && allEcsClusters.clusters.length > 0 ? (
                  <div className="mb-6">
                    <label className="block text-sm text-surface-300 mb-2">Select Container</label>
                    <select
                      value={selectedContainerForApiKey || ''}
                      onChange={(e) => setSelectedContainerForApiKey(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                    >
                      <option value="">-- Select a container --</option>
                      {allEcsClusters.clusters.map((cluster) => (
                        <option key={cluster.clusterArn} value={cluster.clusterName}>
                          {cluster.clusterName} ({cluster.registeredContainerInstancesCount} instances)
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="text-center py-8 text-surface-400">
                    <Container className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No containers available</p>
                    <p className="text-sm text-surface-500 mt-1">Create a container first to manage API keys</p>
                  </div>
                )}

                {/* Create API Key Form */}
                {showCreateApiKey && selectedContainerForApiKey && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-6 p-4 rounded-lg bg-gradient-to-br from-gpu-600/10 to-teal-600/10 border border-gpu-500/20"
                  >
                    <h4 className="text-sm font-semibold text-surface-100 mb-4">Create New API Key</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Key Name</label>
                        <input
                          type="text"
                          value={newApiKeyConfig.name}
                          onChange={(e) => setNewApiKeyConfig({ ...newApiKeyConfig, name: e.target.value })}
                          placeholder={`${selectedContainerForApiKey}-api-key`}
                          className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-surface-300 mb-2">Scopes</label>
                        <div className="space-y-2">
                          {['jobs:read', 'jobs:write', 'jobs:delete', 'usage:read'].map((scope) => (
                            <label key={scope} className="flex items-center gap-2 text-sm text-surface-300">
                              <input
                                type="checkbox"
                                checked={newApiKeyConfig.scopes.includes(scope as ApiKeyScope)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewApiKeyConfig({
                                      ...newApiKeyConfig,
                                      scopes: [...newApiKeyConfig.scopes, scope as ApiKeyScope]
                                    });
                                  } else {
                                    setNewApiKeyConfig({
                                      ...newApiKeyConfig,
                                      scopes: newApiKeyConfig.scopes.filter(s => s !== scope)
                                    });
                                  }
                                }}
                                className="w-4 h-4 rounded border-surface-600"
                              />
                              {scope}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Rate Limit (requests/min)</label>
                          <input
                            type="number"
                            value={newApiKeyConfig.rateLimit}
                            onChange={(e) => setNewApiKeyConfig({ ...newApiKeyConfig, rateLimit: parseInt(e.target.value) || 100 })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-surface-300 mb-2">Expires At (optional)</label>
                          <input
                            type="date"
                            value={newApiKeyConfig.expiresAt}
                            onChange={(e) => setNewApiKeyConfig({ ...newApiKeyConfig, expiresAt: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-surface-900 border border-surface-700 text-surface-100"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => {
                            if (!newApiKeyConfig.name) {
                              toast.error('Please enter an API key name');
                              return;
                            }
                            createApiKeyMutation.mutate({
                              ...newApiKeyConfig,
                              expiresAt: newApiKeyConfig.expiresAt || undefined
                            });
                          }}
                          disabled={createApiKeyMutation.isPending}
                          className="flex-1 btn-primary"
                        >
                          {createApiKeyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create API Key'}
                        </button>
                        <button
                          onClick={() => setShowCreateApiKey(false)}
                          className="flex-1 btn-ghost"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* API Keys List */}
                {selectedContainerForApiKey && apiKeys.length > 0 ? (
                  (() => {
                    // Filter keys by:
                    // 1. Name includes container name (admin-created keys)
                    // 2. clientInfo.gpuContainerId or gpuContainerName matches (client dashboard keys)
                    // 3. clientInfo.source === 'client-dashboard' AND container matches
                    const filteredKeys = apiKeys.filter(key => {
                      const nameMatch = key.name.includes(selectedContainerForApiKey);
                      const clientInfo = key.clientInfo as { gpuContainerId?: string; gpuContainerName?: string; source?: string } | null;
                      const clientMatch = clientInfo && (
                        clientInfo.gpuContainerId === selectedContainerForApiKey ||
                        clientInfo.gpuContainerName?.includes(selectedContainerForApiKey)
                      );
                      return nameMatch || clientMatch;
                    });
                    
                    return filteredKeys.length > 0 ? (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-surface-200">API Keys for {selectedContainerForApiKey}</h4>
                        {filteredKeys.map((key) => {
                          const clientInfo = key.clientInfo as { source?: string; userEmail?: string; workspaceName?: string } | null;
                          const isClientDashboardKey = clientInfo?.source === 'client-dashboard';
                          return (
                            <div key={key.id} className="p-4 rounded-lg bg-surface-800/50 border border-surface-700/50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-medium text-surface-100">{key.name}</span>
                                    <span className={clsx(
                                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                                      key.isActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-surface-600/50 text-surface-300'
                                    )}>
                                      {key.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {isClientDashboardKey && (
                                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
                                        Client Dashboard
                                      </span>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-sm text-surface-400">
                                    <div>
                                      <span className="text-surface-500">Key Prefix:</span> {key.keyPrefix}***
                                    </div>
                                    <div>
                                      <span className="text-surface-500">Rate Limit:</span> {key.rateLimit}/min
                                    </div>
                                    <div>
                                      <span className="text-surface-500">Scopes:</span> {key.scopes.join(', ')}
                                    </div>
                                    {key.lastUsedAt && (
                                      <div>
                                        <span className="text-surface-500">Last Used:</span>{' '}
                                        {formatDistanceToNow(new Date(key.lastUsedAt), { addSuffix: true })}
                                      </div>
                                    )}
                                    {isClientDashboardKey && clientInfo?.userEmail && (
                                      <div className="col-span-2">
                                        <span className="text-surface-500">Created by:</span> {clientInfo.userEmail}
                                        {clientInfo.workspaceName && ` (${clientInfo.workspaceName})`}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {key.isActive ? (
                                    <button
                                      onClick={() => revokeApiKeyMutation.mutate(key.id)}
                                      disabled={revokeApiKeyMutation.isPending}
                                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                      title="Revoke API key"
                                    >
                                      {revokeApiKeyMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <XCircle className="w-4 h-4" />
                                      )}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        if (confirm('Are you sure you want to permanently delete this API key? This action cannot be undone.')) {
                                          deleteApiKeyMutation.mutate(key.id);
                                        }
                                      }}
                                      disabled={deleteApiKeyMutation.isPending}
                                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                      title="Delete API key permanently"
                                    >
                                      {deleteApiKeyMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-surface-400">
                        <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No API keys yet</p>
                        <p className="text-sm text-surface-500 mt-1">Create an API key to authenticate with this container</p>
                      </div>
                    );
                  })()
                ) : selectedContainerForApiKey ? (
                  <div className="text-center py-8 text-surface-400">
                    <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No API keys yet</p>
                    <p className="text-sm text-surface-500 mt-1">Create an API key to authenticate with this container</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Instance Details Modal */}
      {selectedInstanceForDetails && instanceDetails && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface-900 border border-surface-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-surface-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gpu-500 to-teal-500 flex items-center justify-center">
                  <Server className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-surface-100">{instanceDetails.id.slice(0, 12)}...</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={clsx(
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                      instanceDetails.status === 'running' ? 'bg-emerald-500/20 text-emerald-300' :
                      instanceDetails.status === 'pending' || instanceDetails.status === 'provisioning' ? 'bg-yellow-500/20 text-yellow-300' :
                      instanceDetails.status === 'stopping' ? 'bg-yellow-500/20 text-yellow-300' :
                      instanceDetails.status === 'stopped' ? 'bg-orange-500/20 text-orange-300' :
                      instanceDetails.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                      instanceDetails.status === 'cancelled' ? 'bg-gray-500/20 text-gray-300' :
                      'bg-surface-600/50 text-surface-300'
                    )}>
                      {instanceDetails.status}
                    </span>
                    <span className="text-sm text-surface-400">
                      Created {formatDistanceToNow(new Date(instanceDetails.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetchInstanceDetails()}
                  className="btn-ghost p-2"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSelectedInstanceForDetails(null)}
                  className="btn-ghost p-2"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-6">
              {/* Error Display */}
              {instanceDetails.status === 'failed' && instanceDetails.errorMessage && (
                <div className="card bg-red-500/10 border-red-500/30">
                  <h3 className="text-lg font-semibold text-red-300 mb-2">Error</h3>
                  <p className="text-sm text-red-400">{instanceDetails.errorMessage}</p>
                </div>
              )}

              {/* Instance Details Grid */}
              <div className="card">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">Instance Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-surface-400">Type</span>
                    <p className="text-surface-100 font-mono mt-1">{instanceDetails.instanceType}</p>
                  </div>
                  <div>
                    <span className="text-sm text-surface-400">GPUs</span>
                    <p className="text-surface-100 mt-1">{instanceDetails.gpuCount}</p>
                  </div>
                  <div>
                    <span className="text-sm text-surface-400">Pricing</span>
                    <p className="text-surface-100 mt-1">{instanceDetails.useSpot ? 'Spot Instance' : 'On-Demand'}</p>
                  </div>
                  {instanceDetails.instanceId && (
                    <div>
                      <span className="text-sm text-surface-400">Instance ID</span>
                      <p className="text-surface-100 font-mono text-xs mt-1">{instanceDetails.instanceId}</p>
                    </div>
                  )}
                  {instanceDetails.publicIpAddress && (
                    <div>
                      <span className="text-sm text-surface-400">Public IP</span>
                      <p className="text-surface-100 font-mono mt-1">{instanceDetails.publicIpAddress}</p>
                    </div>
                  )}
                  {instanceDetails.privateIpAddress && (
                    <div>
                      <span className="text-sm text-surface-400">Private IP</span>
                      <p className="text-surface-100 font-mono mt-1">{instanceDetails.privateIpAddress}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Cost & Runtime */}
              <div className="card">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">Cost & Runtime</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-surface-400">Estimated Cost</span>
                    <p className="text-2xl font-bold text-surface-100 mt-1">${instanceDetails.estimatedCostUsd.toFixed(2)}<span className="text-sm text-surface-400">/hr</span></p>
                  </div>
                  <div>
                    <span className="text-sm text-surface-400">Max Runtime</span>
                    <p className="text-surface-100 mt-1">{instanceDetails.maxRuntimeHours} hours</p>
                  </div>
                  <div>
                    <span className="text-sm text-surface-400">Environment</span>
                    <p className="text-surface-100 mt-1 capitalize">{instanceDetails.environment}</p>
                  </div>
                  <div>
                    <span className="text-sm text-surface-400">Project</span>
                    <p className="text-surface-100 mt-1">{instanceDetails.project || 'default'}</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="card">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">Timeline</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-gpu-400"></div>
                    <div className="flex-1">
                      <p className="text-sm text-surface-400">Created</p>
                      <p className="text-surface-100">{new Date(instanceDetails.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                  {instanceDetails.startedAt && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                      <div className="flex-1">
                        <p className="text-sm text-surface-400">Started</p>
                        <p className="text-surface-100">{new Date(instanceDetails.startedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                  {instanceDetails.completedAt && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                      <div className="flex-1">
                        <p className="text-sm text-surface-400">Completed</p>
                        <p className="text-surface-100">{new Date(instanceDetails.completedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SSH Access */}
              {instanceDetails.status === 'running' && instanceDetails.publicIpAddress && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-surface-100 mb-4">SSH Access</h3>
                  <p className="text-sm text-surface-400 mb-3">Connect to your instance via SSH:</p>
                  <div className="p-3 rounded-lg bg-surface-800 border border-surface-700 font-mono text-sm">
                    <code className="text-gpu-400">ssh ec2-user@{instanceDetails.publicIpAddress}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`ssh ec2-user@${instanceDetails.publicIpAddress}`);
                        toast.success('SSH command copied to clipboard');
                      }}
                      className="ml-3 px-2 py-1 text-xs bg-gpu-500/20 text-gpu-400 rounded hover:bg-gpu-500/30 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-surface-500 mt-2">
                    Note: Ensure your SSH key is configured and security group allows SSH access on port 22
                  </p>
                </div>
              )}

              {/* Full Job ID */}
              <div className="card">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">Full Job ID</h3>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 rounded-lg bg-surface-800 border border-surface-700 font-mono text-sm text-gpu-400">
                    {instanceDetails.id}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(instanceDetails.id);
                      toast.success('Job ID copied to clipboard');
                    }}
                    className="btn-ghost"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {(instanceDetails.status === 'running' || instanceDetails.status === 'pending' || instanceDetails.status === 'provisioning') && (
                  <button
                    onClick={() => {
                      cancelGpuJobMutation.mutate(instanceDetails.id);
                    }}
                    disabled={pendingJobOperations.has(instanceDetails.id)}
                    className="btn-ghost text-red-400 hover:bg-red-500/10 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {pendingJobOperations.has(instanceDetails.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Power className="w-4 h-4" />
                    )}
                    Terminate Instance
                  </button>
                )}
                {(instanceDetails.status === 'completed' || instanceDetails.status === 'failed' || instanceDetails.status === 'cancelled' || instanceDetails.status === 'terminated') && (
                  <button
                    onClick={() => {
                      setJobToRemove(instanceDetails.id);
                      setConfirmAction('remove-gpu-job');
                    }}
                    disabled={pendingJobOperations.has(instanceDetails.id)}
                    className="btn-ghost text-red-400 hover:bg-red-500/10 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {pendingJobOperations.has(instanceDetails.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Remove Instance
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface-900 border border-surface-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              {(confirmAction === 'start' || confirmAction === 'deploy-ec2' || confirmAction === 'deploy-ecs' || confirmAction === 'run-ecs-task') && (
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  {confirmAction === 'start' ? <Play className="w-6 h-6 text-emerald-400" /> : 
                   confirmAction === 'run-ecs-task' ? <Container className="w-6 h-6 text-emerald-400" /> :
                   <Cloud className="w-6 h-6 text-emerald-400" />}
                </div>
              )}
              {(confirmAction === 'stop' || confirmAction === 'scale-ecs' || confirmAction === 'repair-ecs') && (
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  {confirmAction === 'scale-ecs' ? <BarChart3 className="w-6 h-6 text-blue-400" /> :
                   confirmAction === 'repair-ecs' ? <Settings2 className="w-6 h-6 text-blue-400" /> :
                   <Pause className="w-6 h-6 text-yellow-400" />}
                </div>
              )}
              {confirmAction === 'cleanup-stuck-tasks' && (
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                </div>
              )}
              {(confirmAction === 'cancel-ec2' || confirmAction === 'cancel-ecs') && (
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-orange-400" />
                </div>
              )}
              {(confirmAction === 'terminate' || confirmAction === 'destroy-ec2' || confirmAction === 'destroy-ecs' || confirmAction === 'stop-ecs-task' || confirmAction === 'terminate-instance' || confirmAction === 'terminate-instances-bulk' || confirmAction === 'delete-ecs-cluster' || confirmAction === 'remove-gpu-job') && (
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  {confirmAction === 'terminate' || confirmAction === 'terminate-instance' || confirmAction === 'terminate-instances-bulk' ? <Power className="w-6 h-6 text-red-400" /> : 
                   confirmAction === 'stop-ecs-task' ? <Square className="w-6 h-6 text-red-400" /> :
                   confirmAction === 'delete-ecs-cluster' ? <Container className="w-6 h-6 text-red-400" /> :
                   confirmAction === 'remove-gpu-job' ? <Trash2 className="w-6 h-6 text-red-400" /> :
                   <CloudOff className="w-6 h-6 text-red-400" />}
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-surface-100">
                  {confirmAction === 'start' && 'Start Fleet'}
                  {confirmAction === 'stop' && 'Hibernate Fleet'}
                  {confirmAction === 'terminate' && 'Terminate Fleet'}
                  {confirmAction === 'deploy-ec2' && 'Deploy EC2 Infrastructure'}
                  {confirmAction === 'deploy-ecs' && 'Deploy ECS GPU Infrastructure'}
                  {confirmAction === 'destroy-ec2' && 'Destroy EC2 Infrastructure'}
                  {confirmAction === 'destroy-ecs' && 'Destroy ECS Infrastructure'}
                  {confirmAction === 'cancel-ec2' && 'Stop EC2 Deployment'}
                  {confirmAction === 'cancel-ecs' && 'Stop ECS Deployment'}
                  {confirmAction === 'scale-ecs' && `Scale ${selectedContainerForScaling || 'Container'}`}
                  {confirmAction === 'repair-ecs' && 'Repair ECS Infrastructure'}
                  {confirmAction === 'run-ecs-task' && 'Run GPU Task'}
                  {confirmAction === 'stop-ecs-task' && 'Stop Task'}
                  {confirmAction === 'cleanup-stuck-tasks' && 'Clean Up Stuck Tasks'}
                  {confirmAction === 'terminate-instance' && 'Terminate Instance'}
                  {confirmAction === 'terminate-instances-bulk' && 'Terminate Selected Instances'}
                  {confirmAction === 'delete-ecs-cluster' && 'Delete ECS Cluster'}
                  {confirmAction === 'remove-gpu-job' && 'Remove GPU Instance'}
                </h3>
              </div>
            </div>

            <div className="mb-6 text-surface-400">
              {confirmAction === 'scale-ecs' && selectedContainerForScaling && (
                <p>
                  Scale container <strong>{selectedContainerForScaling}</strong> to{' '}
                  <strong>{containerScaleValues[selectedContainerForScaling]}</strong> GPU instances?
                </p>
              )}
              {confirmAction === 'start' && (
                <p>This will resume all stopped instances. They will begin incurring charges once running.</p>
              )}
              {confirmAction === 'stop' && (
                <p>This will stop all running instances. EBS volumes will be preserved and instances can be restarted later.</p>
              )}
              {confirmAction === 'terminate' && (
                <p className="text-red-400">
                  <strong>Warning:</strong> All instances and their data will be permanently deleted.
                </p>
              )}
              {confirmAction === 'deploy-ecs' && (
                <p>This will deploy ECS GPU infrastructure for containerized workloads.</p>
              )}
              {confirmAction === 'destroy-ecs' && (
                <p className="text-red-400">
                  <strong>Warning:</strong> This will destroy all ECS GPU infrastructure.
                </p>
              )}
              {confirmAction === 'run-ecs-task' && (
                <p>
                  Run a new GPU task
                  {selectedClusterForTaskOp && <> on container <strong>{selectedClusterForTaskOp}</strong></>}?
                </p>
              )}
              {confirmAction === 'stop-ecs-task' && selectedTask && (
                <p>
                  Stop task <span className="font-mono">{selectedTask.taskArn.split('/').pop()}</span>
                  {selectedClusterForTaskOp && <> in cluster <strong>{selectedClusterForTaskOp}</strong></>}?
                </p>
              )}
              {confirmAction === 'cleanup-stuck-tasks' && (
                <div>
                  <p className="mb-2">
                    Stop all {pendingTasksToCleanup.length || ecsClusterDetails?.tasks?.filter(t => t.lastStatus === 'PENDING').length || 0} pending task(s)
                    {selectedClusterForTaskOp && <> in cluster <strong>{selectedClusterForTaskOp}</strong></>}?
                  </p>
                  <p className="text-xs text-surface-500">
                    These tasks are stuck because there are no container instances available to run them. 
                    Stopping them will prevent unnecessary resource consumption.
                  </p>
                </div>
              )}
              {confirmAction === 'terminate-instance' && instanceToDestroy && (
                <p className="text-red-400">
                  <strong>Warning:</strong> Terminate instance <span className="font-mono">{instanceToDestroy}</span>? This action cannot be undone.
                </p>
              )}
              {confirmAction === 'terminate-instances-bulk' && (
                <p className="text-red-400">
                  <strong>Warning:</strong> Terminate {selectedInstances.size} selected instance(s)? This action cannot be undone.
                </p>
              )}
              {confirmAction === 'delete-ecs-cluster' && selectedClusterToDelete && (
                <p className="text-red-400">
                  <strong>Warning:</strong> Delete cluster <span className="font-mono">{selectedClusterToDelete}</span>? 
                  This will stop all tasks, delete all services, terminate all instances, and remove all associated resources. 
                  This action cannot be undone.
                </p>
              )}
              {confirmAction === 'remove-gpu-job' && jobToRemove && (
                <div>
                  <p className="text-red-400 mb-2">
                    <strong>Warning:</strong> Permanently remove this GPU instance record?
                  </p>
                  <p className="text-xs text-surface-500 mb-2">
                    This will delete the job record from the database and clean up all associated AWS resources:
                  </p>
                  <ul className="text-xs text-surface-500 list-disc list-inside space-y-1">
                    <li>SSH key pairs</li>
                    <li>Any orphaned EC2 instances</li>
                    <li>Network infrastructure (VPC, subnets, security groups) if no other jobs exist</li>
                  </ul>
                  <p className="text-xs text-red-400 mt-2">This action cannot be undone.</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (confirmAction === 'scale-ecs' && selectedContainerForScaling) {
                    const desiredCapacity = containerScaleValues[selectedContainerForScaling];
                    ecsApi.scaleCluster(selectedContainerForScaling, desiredCapacity).then(() => {
                      toast.success('Container scaled successfully');
                      refetchAllClusters();
                      setConfirmAction(null);
                      setSelectedContainerForScaling(null);
                    }).catch((error: Error) => {
                      toast.error(`Failed to scale: ${error.message}`);
                    });
                  } else if (confirmAction === 'terminate-instance' && instanceToDestroy && selectedContainerForScaling) {
                    terminateEcsInstanceMutation.mutate({
                      clusterName: selectedContainerForScaling,
                      instanceId: instanceToDestroy
                    });
                  } else if (confirmAction === 'terminate-instances-bulk' && expandedContainer) {
                    terminateBulkInstancesMutation.mutate({
                      clusterName: expandedContainer,
                      instanceIds: Array.from(selectedInstances)
                    });
                  } else if (confirmAction === 'delete-ecs-cluster' && selectedClusterToDelete) {
                    deleteEcsClusterMutation.mutate(selectedClusterToDelete);
                  } else if (confirmAction === 'remove-gpu-job' && jobToRemove) {
                    removeGpuJobMutation.mutate(jobToRemove);
                    setConfirmAction(null);
                  } else {
                    handleConfirmAction();
                  }
                }}
                disabled={isAnyMutationLoading || (confirmAction === 'remove-gpu-job' && !!jobToRemove && pendingJobOperations.has(jobToRemove))}
                className="flex-1 btn-primary"
              >
                {(isAnyMutationLoading || (confirmAction === 'remove-gpu-job' && !!jobToRemove && pendingJobOperations.has(jobToRemove))) ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setConfirmAction(null);
                  setSelectedContainerForScaling(null);
                  setInstanceToDestroy(null);
                  setSelectedClusterToDelete(null);
                  setSelectedTask(null);
                  setSelectedClusterForTaskOp(null);
                  setPendingTasksToCleanup([]);
                  setJobToRemove(null);
                }}
                disabled={isAnyMutationLoading}
                className="flex-1 btn-ghost"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* The rest of the old modal code should be removed */}
      {/* Let me find where the old confirmation modal starts to ensure we're not duplicating */}
    </motion.div>
  );
}
